import { useParams, useNavigate, Link } from "react-router-dom";
import { getDatabase, ref, get, set, update } from "firebase/database";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import { useEffect, useState, useCallback, useRef } from "react";
import { Slate, Editable, withReact } from 'slate-react';
import { createEditor, Editor, Transforms, Element as SlateElement } from 'slate';
import { withHistory } from 'slate-history';
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import Navbar from "../components/NavBar";
import Footer from "../components/Footer";
import useTitle from "../components/Title";
import { toast } from "../components/toast";
import '../components/eventCreate/eventCreate.css';
import '../components/groupEventCard/groupEventCard.css';

function Group() {
  const { groupSlug } = useParams();
  const navigate = useNavigate();

  const [group, setGroup] = useState(null);
  const [, setGroupOwner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [isMember, setIsMember] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("AUTOMOTIVE");
  const [groupEvents, setGroupEvents] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarView, setCalendarView] = useState(window.innerWidth <= 768 ? "dayGridWeek" : "dayGridMonth");
  const [groupId, setGroupId] = useState(null);
  const [hasRequestedJoin, setHasRequestedJoin] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [userRank, setUserRank] = useState(null);

  // Edit states
  const [showEdit, setShowEdit] = useState(false);
  const [editGroupPublic, setEditGroupPublic] = useState("Y");
  const [editGroupJoinOnly, setEditGroupJoinOnly] = useState("N");
  const [editGroupInstagram, setEditGroupInstagram] = useState("");
  const [editGroupYoutube, setEditGroupYoutube] = useState("");
  const [editGroupTwitter, setEditGroupTwitter] = useState("");
  const [editShowMemberCount, setEditShowMemberCount] = useState("Y");
  const [editEditor] = useState(() => withHistory(withReact(createEditor())));
  const [editEditorValue, setEditEditorValue] = useState([
      {
        type: 'paragraph',
        children: [{ text: '' }],
      },
    ]);

  // Members list
  const [groupMembers, setGroupMembers] = useState([]);
  const [memberRoleEdit, setMemberRoleEdit] = useState({});
  
  // Announcements
  const [announcements, setAnnouncements] = useState([]);
  const [showNewAnnouncementForm, setShowNewAnnouncementForm] = useState(false);
  const [announcementTitle, setAnnouncementTitle] = useState("");
  const announcementEditorRef = useRef(null);
  const [announcementEditorValue, setAnnouncementEditorValue] = useState([
    {
      type: 'paragraph',
      children: [{ text: '' }],
    },
  ]);
  const [editingAnnouncementId, setEditingAnnouncementId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [allowCommentsToggle, setAllowCommentsToggle] = useState("Y");
  const [commentTexts, setCommentTexts] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  const [expandedReplies, setExpandedReplies] = useState({});
  const [replyTexts, setReplyTexts] = useState({});
  const [deleteConfirmModalId, setDeleteConfirmModalId] = useState(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showYoutubeModal, setShowYoutubeModal] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  
  const calendarRef = useRef(null);

  // Initialize announcement editor on component mount
  useEffect(() => {
    announcementEditorRef.current = withHistory(withReact(createEditor()));
  }, []);

  // Slate editor helpers for edit
  const isEditMarkActive = (editor, format) => {
    const marks = Editor.marks(editor);
    return marks ? marks[format] === true : false;
  };

  const isEditBlockActive = (editor, format) => {
    const { selection } = editor;
    if (!selection) return false;
    const [match] = Editor.nodes(editor, {
      match: n => SlateElement.isElement(n) && n.type === format,
    });
    return !!match;
  };

  const toggleEditMark = (format) => {
    const isActive = isEditMarkActive(editEditor, format);
    if (isActive) {
      Editor.removeMark(editEditor, format);
    } else {
      Editor.addMark(editEditor, format, true);
    }
  };

  const toggleEditBlock = (format) => {
    const isActive = isEditBlockActive(editEditor, format);
    const isList = ['bulleted-list', 'numbered-list'].includes(format);

    Transforms.unwrapNodes(editEditor, {
      match: n => ['bulleted-list', 'numbered-list'].includes(SlateElement.isElement(n) ? n.type : ''),
      split: true,
    });

    let newProperties;
    if (isList) {
      newProperties = {
        type: isActive ? 'paragraph' : format,
      };
    } else {
      newProperties = {
        type: isActive ? 'paragraph' : format,
      };
    }
    Transforms.setNodes(editEditor, newProperties);
  };

  // Slate editor button components
  const EditMarkButton = ({ format, label }) => {
    const isActive = isEditMarkActive(editEditor, format);
    return (
      <button
        onMouseDown={(e) => {
          e.preventDefault();
          toggleEditMark(format);
        }}
        style={{
          backgroundColor: isActive ? '#FE5533' : 'var(--bg)',
        }}
        className="rich-edit-button"
        dangerouslySetInnerHTML={{ __html: label }}
      />
    );
  };

  const EditBlockButton = ({ format, label }) => {
    const isActive = isEditBlockActive(editEditor, format);
    return (
      <button
        onMouseDown={(e) => {
          e.preventDefault();
          toggleEditBlock(format);
        }}
        style={{
          backgroundColor: isActive ? '#FE5533' : 'var(--bg)',
        }}

        className="rich-edit-button"
        dangerouslySetInnerHTML={{ __html: label }}
      />
    );
  };

  // Announcement editor helpers
  const isAnnMarkActive = (format) => {
    if (!announcementEditorRef.current) return false;
    const marks = Editor.marks(announcementEditorRef.current);
    return marks ? marks[format] === true : false;
  };

  const isAnnBlockActive = (format) => {
    if (!announcementEditorRef.current) return false;
    const { selection } = announcementEditorRef.current;
    if (!selection) return false;
    const [match] = Editor.nodes(announcementEditorRef.current, {
      match: n => SlateElement.isElement(n) && n.type === format,
    });
    return !!match;
  };

  const toggleAnnMark = (format) => {
    if (!announcementEditorRef.current) return;
    const isActive = isAnnMarkActive(format);
    if (isActive) {
      Editor.removeMark(announcementEditorRef.current, format);
    } else {
      Editor.addMark(announcementEditorRef.current, format, true);
    }
  };

  const toggleAnnBlock = (format) => {
    if (!announcementEditorRef.current) return;
    const isActive = isAnnBlockActive(format);
    const isList = ['bulleted-list', 'numbered-list'].includes(format);

    Transforms.unwrapNodes(announcementEditorRef.current, {
      match: n => ['bulleted-list', 'numbered-list'].includes(SlateElement.isElement(n) ? n.type : ''),
      split: true,
    });

    let newProperties;
    if (isList) {
      newProperties = {
        type: isActive ? 'paragraph' : format,
      };
    } else {
      newProperties = {
        type: isActive ? 'paragraph' : format,
      };
    }
    Transforms.setNodes(announcementEditorRef.current, newProperties);
  };

  // Announcement editor button components
  const AnnMarkButton = ({ format, label }) => {
    const isActive = isAnnMarkActive(format);
    return (
      <button
        onMouseDown={(e) => {
          e.preventDefault();
          toggleAnnMark(format);
        }}
        style={{
          backgroundColor: isActive ? '#FE5533' : 'var(--bg)',
        }}
        className="rich-edit-button"
        dangerouslySetInnerHTML={{ __html: label }}
      />
    );
  };

  const AnnBlockButton = ({ format, label }) => {
    const isActive = isAnnBlockActive(format);
    return (
      <button
        onMouseDown={(e) => {
          e.preventDefault();
          toggleAnnBlock(format);
        }}
        style={{
          backgroundColor: isActive ? '#FE5533' : 'var(--bg)',
        }}
        className="rich-edit-button"
        dangerouslySetInnerHTML={{ __html: label }}
      />
    );
  };

  const AnnYoutubeButton = () => {
    return (
      <button
        onMouseDown={(e) => {
          e.preventDefault();
          setShowYoutubeModal(true);
        }}
        style={{
          backgroundColor: 'var(--bg)',
        }}
        className="rich-edit-button"
        title="Insert YouTube video"
        dangerouslySetInnerHTML={{ __html: '<i class="bi bi-play-circle"></i>' }}
      />
    );
  };

    // Preload editor value whenever event data loads (so data is ready before clicking Edit)
    useEffect(() => {
      if (group && group.GROUP_DESC) {
        try {
          const parsed = JSON.parse(group.GROUP_DESC);
          if (Array.isArray(parsed)) {
            setEditEditorValue(parsed);
          } else {
            setEditEditorValue([
              {
                type: 'paragraph',
                children: [{ text: group.GROUP_DESC }],
              },
            ]);
          }
        } catch (error) {
          // Plain text fallback
          setEditEditorValue([
            {
              type: 'paragraph',
              children: [{ text: group.GROUP_DESC }],
            },
          ]);
        }
      } else if (group && !group.GROUP_DESC) {
        // Reset to empty if event has no description
        setEditEditorValue([
          {
            type: 'paragraph',
            children: [{ text: '' }],
          },
        ]);
      }
    }, [group]);
  

  // Format date string (dd/m/yyyy hh -> formatted date)
  // const formatDate = (dateStr) => {
  //   if (!dateStr) return '';
  //   const [date] = dateStr.split(' ');
  //   const [day, month, year] = date.split('/');
  //   const dateObj = new Date(year, month - 1, day);
  //   return dateObj.toLocaleDateString('en-US', { 
  //     weekday: 'long', 
  //     year: 'numeric', 
  //     month: 'long', 
  //     day: 'numeric' 
  //   });
  // };

  // Get event background image
  const getEventBackgroundImage = useCallback((event) => {
    const type = (event.EVENT_TYPE || "other").toLowerCase();
    const imgNUM = event.EVENT_IMAGENUM || "";
    const customImage = event.EVENT_IMAGE || null;

    const imageMap = {
      race: `race${imgNUM}`,
      rally: `rally${imgNUM}`,
      meetup: `meetup${imgNUM}`,
      drag: `drag${imgNUM}`,
      drift: `drift${imgNUM}`,
      trackday: `trackday${imgNUM}`,
      motorbike: `motorbike${imgNUM}`,
      hockey: `hockey${imgNUM}`,
      football: `football${imgNUM}`,
      basketball: `basketball${imgNUM}`,
      swimming: `swimming${imgNUM}`,
      festivals: `festivals${imgNUM}`,
      rock: `rock${imgNUM}`,
      indie: `indie${imgNUM}`,
      jazz: `jazz${imgNUM}`,
      dj: `dj${imgNUM}`,
      drumandbass: `drumandbass${imgNUM}`,
      countryfolk: `countryfolk${imgNUM}`,
      metal: `metal${imgNUM}`,
      pop: `pop${imgNUM}`,
      other: `other${imgNUM}`,
    };

    const presetImg = imgNUM ? imageMap[type] || imageMap.other : null;

    // Custom image
    if (customImage) {
      return `/assets/img/uploads/events/${event.EVENT_IMAGE}.png`;
    }

    // Preset image
    if (presetImg) {
      return `/assets/img/${type}/${presetImg}-640.png`;
    }

    // No image
    return null;
  }, []);

  // Convert groupEvents to FullCalendar format
  const convertToCalendarEvents = useCallback((events) => {
    return events
      .filter((e) => e.EVENT_DATE)
      .map((e) => {
        const [day, month, year] = e.EVENT_DATE.split("/");
        const bgImage = getEventBackgroundImage(e);
        return {
          id: e.eventId,
          title: e.EVENT_NAME,
          date: `${year}-${month}-${day}`,
          extendedProps: {
            slug: e.EVENT_SLUG,
            urlkey: e.EVENT_URLKEY,
            location: e.EVENT_LOCATION,
            postcode: e.EVENT_POSTCODE,
            type: e.EVENT_TYPE,
            imageNum: e.EVENT_IMAGE || e.EVENT_IMAGENUM,
            verified: e.EVENT_VERIFIED,
            soldOut: e.EVENT_SOLDOUT,
            description: e.EVENT_DESCRIPTION,
            price: e.EVENT_PRICE,
            backgroundImage: bgImage,
          },
        };
      });
  }, [getEventBackgroundImage]);

  // Update calendar events when groupEvents or selectedCategory changes
  useEffect(() => {
    const filtered = groupEvents.filter((event) => event.groupCategory === selectedCategory);
    const calEvents = convertToCalendarEvents(filtered);
    setCalendarEvents(calEvents);
  }, [groupEvents, selectedCategory, convertToCalendarEvents]);

  // Handle calendar view change on window resize
  useEffect(() => {
    const handleResize = () => {
      const newView = window.innerWidth <= 768 ? "dayGridWeek" : "dayGridMonth";
      setCalendarView(newView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle FullCalendar rendering when tab becomes visible
  useEffect(() => {
    const tabElement = document.getElementById('nav-groupattending-tab');
    if (!tabElement) return;

    const handleTabShow = () => {
      // Trigger calendar re-render when tab becomes visible and has events
      if (calendarEvents.length > 0) {
        setTimeout(() => {
          if (calendarRef.current?.getApi) {
            calendarRef.current.getApi().render();
          }
        }, 100);
      }
    };

    tabElement.addEventListener('shown.bs.tab', handleTabShow);
    return () => tabElement.removeEventListener('shown.bs.tab', handleTabShow);
  }, [calendarEvents]);

  // Re-render calendar when calendarEvents change
  useEffect(() => {
    if (calendarEvents.length > 0 && calendarRef.current?.getApi) {
      setTimeout(() => {
        calendarRef.current.getApi().render();
      }, 100);
    }
  }, [calendarEvents]);

    // Convert Slate JSON to readable text
  const convertSlateToJSX = (description) => {
    try {
      const parsed = JSON.parse(description);
      
      // Check if it's a Slate document (array of blocks)
      if (Array.isArray(parsed)) {
        return parsed.map((block, idx) => {
          if (!block.children || !Array.isArray(block.children)) return null;
          
          const children = block.children.map((child, cidx) => {
            let text = child.text || "";
            if (child.bold) text = <strong key={`${idx}-${cidx}-bold`}>{text}</strong>;
            if (child.italic) text = <em key={`${idx}-${cidx}-italic`}>{text}</em>;
            if (child.underline) text = <u key={`${idx}-${cidx}-under`}>{text}</u>;
            return text;
          });
          
          switch (block.type) {
            case "heading-one":
              return <h1 key={idx} className="text-header1" style={{ fontSize: '24px', fontWeight: 'bold', margin: '15px 0 10px 0' }}>{children}</h1>;
            case "heading-two":
              return <h2 key={idx} className="text-header2" style={{ fontSize: '20px', fontWeight: 'bold', margin: '12px 0 8px 0' }}>{children}</h2>;
            case "heading-three":
              return <h3 key={idx} className="text-header3" style={{ fontSize: '16px', fontWeight: 'bold', margin: '10px 0 6px 0' }}>{children}</h3>;
            case "bulleted-list":
              return <li key={idx} className="text-bulleted-list" style={{ marginLeft: '20px', listStyleType: 'disc' }}>{children}</li>;
            case "numbered-list":
              return <li key={idx} className="text-numbered-list" style={{ marginLeft: '20px', listStyleType: 'decimal' }}>{children}</li>;
            default:
              return <p key={idx} className="event-text" style={{ margin: '8px 0' }}>{children}</p>;
          }
        });
      }
      
      // If it's not a Slate document, return as plain text
      return description;
    } catch (error) {
      // If parsing fails, it's plain text
      return description;
    }
  };

  // Extract YouTube video ID from URL
  const extractYouTubeId = (url) => {
    if (!url) return null;
    const regex = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?\n]*).*/;
    const match = url.match(regex);
    return match && match[7]?.length === 11 ? match[7] : null;
  };

  // Insert YouTube embed into announcement editor
  const insertYoutubeEmbed = () => {
    const videoId = extractYouTubeId(youtubeUrl);
    if (!videoId) {
      alert("Invalid YouTube URL. Please check and try again.");
      return;
    }

    const youtubeElement = {
      type: 'youtube',
      videoId: videoId,
      children: [{ text: '' }],
    };

    const editor = announcementEditorRef.current;
    Transforms.insertNodes(editor, youtubeElement);
    setYoutubeUrl("");
    setShowYoutubeModal(false);
  };

  // Update convertSlateToJSX to handle YouTube embeds
  const convertSlateToJSXWithYoutube = (description) => {
    try {
      const parsed = JSON.parse(description);
      
      if (Array.isArray(parsed)) {
        return parsed.map((block, idx) => {
          // Handle YouTube embeds
          if (block.type === 'youtube') {
            return (
              <div key={idx} style={{ margin: '16px 0', borderRadius: '8px', overflow: 'hidden' }}>
                <iframe
                  width="100%"
                  height="425"
                  src={`https://www.youtube.com/embed/${block.videoId}`}
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ maxWidth: '100%' }}
                ></iframe>
              </div>
            );
          }

          if (!block.children || !Array.isArray(block.children)) return null;
          
          const children = block.children.map((child, cidx) => {
            let text = child.text || "";
            if (child.bold) text = <strong key={`${idx}-${cidx}-bold`}>{text}</strong>;
            if (child.italic) text = <em key={`${idx}-${cidx}-italic`}>{text}</em>;
            if (child.underline) text = <u key={`${idx}-${cidx}-under`}>{text}</u>;
            return text;
          });
          
          switch (block.type) {
            case "heading-one":
              return <h1 key={idx} className="text-header1" style={{ fontSize: '24px', fontWeight: 'bold', margin: '15px 0 10px 0' }}>{children}</h1>;
            case "heading-two":
              return <h2 key={idx} className="text-header2" style={{ fontSize: '20px', fontWeight: 'bold', margin: '12px 0 8px 0' }}>{children}</h2>;
            case "heading-three":
              return <h3 key={idx} className="text-header3" style={{ fontSize: '16px', fontWeight: 'bold', margin: '10px 0 6px 0' }}>{children}</h3>;
            case "bulleted-list":
              return <li key={idx} className="text-bulleted-list" style={{ marginLeft: '20px', listStyleType: 'disc' }}>{children}</li>;
            case "numbered-list":
              return <li key={idx} className="text-numbered-list" style={{ marginLeft: '20px', listStyleType: 'decimal' }}>{children}</li>;
            default:
              return <p key={idx} className="event-text" style={{ margin: '8px 0' }}>{children}</p>;
          }
        });
      }
      
      return description;
    } catch (error) {
      return description;
    }
  };

  const ledescription = group?.GROUP_DESC 
    ? convertSlateToJSX(group.GROUP_DESC)
    : "It appears that the event organizer has not yet provided a description.";


  // Handle joining a group
  const handleJoinGroup = async () => {
    if (!currentUser) {
      toast("Please log in to join a group.", "#", "warning");
      navigate("/Login");
      return;
    }

    setIsJoining(true);
    try {
      const db = getDatabase();
      const now = new Date();
      const day = now.getDate();
      const month = now.getMonth() + 1;
      const year = now.getFullYear();
      const hour = now.getHours();
      const JOINED_DATE = `${day}/${month}/${year} ${hour}`;

      // Find the group ID (key in GROUPS)
      const groupsRef = ref(db, "GROUPS");
      const snapshot = await get(groupsRef);
      let groupId = null;

      if (snapshot.exists()) {
        const groupsData = snapshot.val();
        const foundEntry = Object.entries(groupsData).find(
          ([_, groupData]) => groupData.GROUP_SLUG === groupSlug
        );
        if (foundEntry) {
          groupId = foundEntry[0];
        }
      }

      if (!groupId) {
        toast("Error finding group.", "#", "danger");
        setIsJoining(false);
        return;
      }

      // Add member to group's MEMBERS
      const memberData = {
        UID: currentUser.uid,
        GROUP_RANK: "MEMBER",
        JOINED_DATE,
      };
      await set(ref(db, `GROUPS/${groupId}/MEMBERS/${currentUser.uid}`), memberData);

      // Add group reference to user's GROUPS
      await set(ref(db, `USERS/${currentUser.uid}/GROUPS/${groupId}/${groupSlug}`), {
        GROUP_ID: groupId,
        GROUP_NAME: group.GROUP_NAME,
        GROUP_SLUG: groupSlug,
        GROUP_RANK: "MEMBER",
        JOINED_DATE,
      });

      setIsMember(true);
      toast("Successfully joined the group!", "#", "success");
    } catch (error) {
      console.error("Error joining group:", error);
      toast("Error joining group. Please try again.", "#", "danger");
    } finally {
      setIsJoining(false);
    }
  };

  // Handle leaving a group
  const handleLeaveGroup = async () => {
    if (!currentUser) return;

    setIsJoining(true);
    try {
      const db = getDatabase();

      // Find the group ID
      const groupsRef = ref(db, "GROUPS");
      const snapshot = await get(groupsRef);
      let groupId = null;

      if (snapshot.exists()) {
        const groupsData = snapshot.val();
        const foundEntry = Object.entries(groupsData).find(
          ([_, groupData]) => groupData.GROUP_SLUG === groupSlug
        );
        if (foundEntry) {
          groupId = foundEntry[0];
        }
      }

      if (!groupId) {
        toast("Error finding group.", "#", "danger");
        setIsJoining(false);
        return;
      }

      // Remove member from group's MEMBERS
      await set(ref(db, `GROUPS/${groupId}/MEMBERS/${currentUser.uid}`), null);

      // Remove group reference from user's GROUPS
      await set(ref(db, `USERS/${currentUser.uid}/GROUPS/${groupId}/${groupSlug}`), null);

      setIsMember(false);
      toast("Successfully left the group!", "#", "success");
    } catch (error) {
      console.error("Error leaving group:", error);
      toast("Error leaving group. Please try again.", "#", "danger");
    } finally {
      setIsJoining(false);
    }
  };

  // Handle requesting to join a join-only group
  const handleRequestJoin = async () => {
    if (!currentUser) {
      toast("Please log in to request access.", "#", "warning");
      navigate("/Login");
      return;
    }

    setIsJoining(true);
    try {
      const db = getDatabase();
      const now = new Date();
      const day = now.getDate();
      const month = now.getMonth() + 1;
      const year = now.getFullYear();
      const hour = now.getHours();
      const REQUEST_DATE = `${day}/${month}/${year} ${hour}`;

      // Add to GROUPS/{groupId}/WHITELIST/{uid}
      await set(ref(db, `GROUPS/${groupId}/WHITELIST/${currentUser.uid}`), {
        UID: currentUser.uid,
        REQUEST_DATE,
        APPROVED: "N",
      });

      setHasRequestedJoin(true);
      toast("Join request sent! Waiting for approval.", "#", "success");
    } catch (error) {
      console.error("Error requesting to join:", error);
      toast("Error sending join request. Please try again.", "#", "danger");
    } finally {
      setIsJoining(false);
    }
  };

  // Handle accepting a member from whitelist
  const handleAcceptMember = async (userId) => {
    try {
      const db = getDatabase();
      const now = new Date();
      const day = now.getDate();
      const month = now.getMonth() + 1;
      const year = now.getFullYear();
      const hour = now.getHours();
      const JOINED_DATE = `${day}/${month}/${year} ${hour}`;

      // Add to GROUPS/{groupId}/MEMBERS
      const memberData = {
        UID: userId,
        GROUP_RANK: "MEMBER",
        JOINED_DATE,
      };
      await set(ref(db, `GROUPS/${groupId}/MEMBERS/${userId}`), memberData);

      // Remove from whitelist
      await set(ref(db, `GROUPS/${groupId}/WHITELIST/${userId}`), null);

      // Add group reference to user's GROUPS
      await set(ref(db, `USERS/${userId}/GROUPS/${groupId}/${group.GROUP_SLUG}`), {
        GROUP_ID: groupId,
        GROUP_NAME: group.GROUP_NAME,
        GROUP_SLUG: group.GROUP_SLUG,
        GROUP_RANK: "MEMBER",
        JOINED_DATE,
      });



      toast("Member approved!", "#", "success");
      
      // Refresh pending requests from database
      setTimeout(() => {
        fetchPendingRequests();
      }, 100);
    } catch (error) {
        // console.error("Error accepting member:", );
    }
  };

  // Handle rejecting a member from whitelist
  const handleRejectMember = async (userId) => {
    try {
      const db = getDatabase();
      
      // Remove from whitelist
      await set(ref(db, `GROUPS/${groupId}/WHITELIST/${userId}`), null);

      toast("Member request rejected.", "#", "success");
      
      // Refresh pending requests from database
      setTimeout(() => {
        fetchPendingRequests();
      }, 100);
    } catch (error) {
      console.error("Error rejecting member:", error);
      toast("Error rejecting member. Please try again.", "#", "danger");
    }
  };

  // Handle updating member role
  const handleUpdateMemberRole = async (userId, newRole) => {
    try {
      // Check permissions
      if (userRank !== "OWNER" && userRank !== "ADMIN") {
        toast("You don't have permission to update roles.", "#", "danger");
        return;
      }

      // ADMINs cannot change OWNER or other ADMIN roles
      const targetMember = groupMembers.find(m => m.userId === userId);
      if (userRank === "ADMIN") {
        if (targetMember?.GROUP_RANK === "OWNER" || targetMember?.GROUP_RANK === "ADMIN") {
          toast("Admins cannot update Owner or Admin roles.", "#", "danger");
          return;
        }
        // ADMINs can only change members to MEMBER or ADMIN
        if (newRole === "OWNER") {
          toast("Admins cannot promote members to Owner.", "#", "danger");
          return;
        }
      }

      const db = getDatabase();
      
      // Update role in GROUPS
      await set(ref(db, `GROUPS/${groupId}/MEMBERS/${userId}/GROUP_RANK`), newRole);
      
      // Update role in USERS (must include groupSlug in path)
      await set(ref(db, `USERS/${userId}/GROUPS/${groupId}/${group.GROUP_SLUG}/GROUP_RANK`), newRole);

      toast("Member role updated successfully!", "#", "success");
      
      // Refresh group members
      setTimeout(() => {
        fetchGroupMembers();
      }, 100);
    } catch (error) {
      console.error("Error updating member role:", error);
      toast("Error updating member role. Please try again.", "#", "danger");
    }
  };

  // Handle removing a member
  const handleRemoveMember = async (userId) => {
    try {
      // Check permissions
      if (userRank !== "OWNER" && userRank !== "ADMIN") {
        toast("You don't have permission to remove members.", "#", "danger");
        return;
      }

      // ADMINs cannot remove OWNER or other ADMIN
      const targetMember = groupMembers.find(m => m.userId === userId);
      if (userRank === "ADMIN") {
        if (targetMember?.GROUP_RANK === "OWNER" || targetMember?.GROUP_RANK === "ADMIN") {
          toast("Admins cannot remove Owner or Admin members.", "#", "danger");
          return;
        }
      }

      // Confirm removal
      if (!window.confirm(`Remove ${targetMember?.userData?.USERDISPLAYNAME || "this member"} from the group?`)) {
        return;
      }

      const db = getDatabase();
      
      // Remove from GROUPS
      await set(ref(db, `GROUPS/${groupId}/MEMBERS/${userId}`), null);
      
      // Remove from USERS (must include groupSlug in path)
      await set(ref(db, `USERS/${userId}/GROUPS/${groupId}/${group.GROUP_SLUG}`), null);

      toast("Member removed successfully!", "#", "success");
      
      // Refresh group members
      setTimeout(() => {
        fetchGroupMembers();
      }, 100);
    } catch (error) {
      console.error("Error removing member:", error);
      toast("Error removing member. Please try again.", "#", "danger");
    }
  };

  // Fetch pending whitelist requests
  const fetchPendingRequests = useCallback(async () => {
    if (!groupId || !userRank || (userRank !== "OWNER" && userRank !== "ADMIN")) {
      setPendingRequests([]);
      return;
    }

    try {
      const db = getDatabase();
      const whitelistRef = ref(db, `GROUPS/${groupId}/WHITELIST`);
      const snapshot = await get(whitelistRef);

      if (snapshot.exists()) {
        const whitelistData = snapshot.val();
        const requests = [];

        // Get user details for each pending request
        for (const userId in whitelistData) {
          const userRef = ref(db, `USERS/${userId}`);
          const userSnap = await get(userRef);
          if (userSnap.exists()) {
            requests.push({
              userId,
              ...whitelistData[userId],
              userData: userSnap.val(),
            });
          } else {
            requests.push({
              userId,
              ...whitelistData[userId],
              userData: { USERDISPLAYNAME: "Unknown User" },
            });
          }
        }

        setPendingRequests(requests);
      } else {
        setPendingRequests([]);
      }
    } catch (error) {
      console.error("Error fetching pending requests:", error);
      setPendingRequests([]);
    }
  }, [groupId, userRank]);

  // Handle saving group changes
  const handleSaveGroupChanges = async () => {
    try {
      // console.log("Saving description:", JSON.stringify(editEditorValue));
      const db = getDatabase();
      const updatedGroup = {
        GROUP_PUBLIC: editGroupPublic,
        GROUP_JOINONLY: editGroupJoinOnly,
        GROUP_DESC: JSON.stringify(editEditorValue),
        GROUP_INSTAGRAM: editGroupInstagram,
        GROUP_YOUTUBE: editGroupYoutube,
        GROUP_TWITTER: editGroupTwitter,
        GROUP_SHOWMEMBERCOUNT: editShowMemberCount,
      };

      await update(ref(db, `GROUPS/${groupId}`), updatedGroup);
      setGroup((prev) => ({ ...prev, ...updatedGroup }));
      setShowEdit(false);
      toast("Group settings updated successfully!", "#", "success");
    } catch (error) {
      console.error("Error saving group changes:", error);
      toast("Error saving group settings. Please try again.", "#", "danger");
    }
  };

  // Fetch group members
  const fetchGroupMembers = useCallback(async () => {
    if (!groupId || !group) {
      setGroupMembers([]);
      return;
    }

    try {
      const db = getDatabase();
      const membersRef = ref(db, `GROUPS/${groupId}/MEMBERS`);
      const snapshot = await get(membersRef);

      const members = [];

      // First, add the owner if they exist
      if (group.GROUP_OWNER) {
        const ownerRef = ref(db, `USERS/${group.GROUP_OWNER}`);
        const ownerSnap = await get(ownerRef);
        if (ownerSnap.exists()) {
          members.push({
            userId: group.GROUP_OWNER,
            UID: group.GROUP_OWNER,
            GROUP_RANK: "OWNER",
            JOINED_DATE: "Owner",
            userData: ownerSnap.val(),
          });
        }
      }

      // Then add other members from the MEMBERS list
      if (snapshot.exists()) {
        const membersData = snapshot.val();

        // Get user details for each member
        for (const userId in membersData) {
          // Skip if this is the owner (already added above)
          if (userId === group.GROUP_OWNER) continue;

          const userRef = ref(db, `USERS/${userId}`);
          const userSnap = await get(userRef);
          if (userSnap.exists()) {
            members.push({
              userId,
              ...membersData[userId],
              userData: userSnap.val(),
            });
          } else {
            members.push({
              userId,
              ...membersData[userId],
              userData: { USERDISPLAYNAME: "Unknown User" },
            });
          }
        }
      }

      setGroupMembers(members);
    } catch (error) {
      console.error("Error fetching group members:", error);
      setGroupMembers([]);
    }
  }, [groupId, group]);

  // Fetch announcements
  const fetchAnnouncements = useCallback(async () => {
    if (!groupId) {
      setAnnouncements([]);
      return;
    }

    try {
      const db = getDatabase();
      const announcementsRef = ref(db, `GROUPS/${groupId}/ANNOUNCEMENTS`);
      const snapshot = await get(announcementsRef);

      if (snapshot.exists()) {
        const announcementsData = snapshot.val();
        const announcementsList = [];

        for (const annId in announcementsData) {
          const ann = announcementsData[annId];
          
          // Fetch creator details
          let creatorData = { USERDISPLAYNAME: "Unknown User" };
          if (ann.CREATED_BY) {
            const creatorRef = ref(db, `USERS/${ann.CREATED_BY}`);
            const creatorSnap = await get(creatorRef);
            if (creatorSnap.exists()) {
              creatorData = creatorSnap.val();
            }
          }

          // Fetch comment creator details and replies
          let comments = null;
          if (ann.COMMENTS) {
            comments = {};
            for (const commentId in ann.COMMENTS) {
              const comment = ann.COMMENTS[commentId];
              let commentCreatorData = { USERDISPLAYNAME: "Unknown User" };
              
              if (comment.CREATED_BY) {
                const commentCreatorRef = ref(db, `USERS/${comment.CREATED_BY}`);
                const commentCreatorSnap = await get(commentCreatorRef);
                if (commentCreatorSnap.exists()) {
                  commentCreatorData = commentCreatorSnap.val();
                }
              }

              // Fetch replies and reply creator details
              let replies = null;
              if (comment.REPLIES) {
                replies = {};
                for (const replyId in comment.REPLIES) {
                  const reply = comment.REPLIES[replyId];
                  let replyCreatorData = { USERDISPLAYNAME: "Unknown User" };
                  
                  if (reply.CREATED_BY) {
                    const replyCreatorRef = ref(db, `USERS/${reply.CREATED_BY}`);
                    const replyCreatorSnap = await get(replyCreatorRef);
                    if (replyCreatorSnap.exists()) {
                      replyCreatorData = replyCreatorSnap.val();
                    }
                  }
                  
                  replies[replyId] = {
                    ...reply,
                    creatorData: replyCreatorData,
                  };
                }
              }
              
              comments[commentId] = {
                ...comment,
                creatorData: commentCreatorData,
                REPLIES: replies,
              };
            }
          }

          announcementsList.push({
            id: annId,
            ...ann,
            COMMENTS: comments,
            creatorData,
          });
        }

        // Sort by date, newest first
        announcementsList.sort((a, b) => {
          // Parse DD/MM/YYYY HH:MM format
          const parseDate = (dateStr) => {
            const [datePart, timePart] = dateStr.split(' ');
            const [day, month, year] = datePart.split('/');
            const [hour, minute] = timePart ? timePart.split(':') : ['0', '0'];
            return new Date(year, month - 1, day, hour, minute);
          };
          
          const dateA = parseDate(a.CREATED_DATE || "01/01/2000 00:00");
          const dateB = parseDate(b.CREATED_DATE || "01/01/2000 00:00");
          return dateB - dateA;
        });

        setAnnouncements(announcementsList);
      } else {
        setAnnouncements([]);
      }
    } catch (error) {
      console.error("Error fetching announcements:", error);
      setAnnouncements([]);
    }
  }, [groupId]);

  // Create new announcement
  const handleCreateAnnouncement = async () => {
    if (!announcementTitle.trim() || !announcementEditorValue.some(block => block.children?.some(child => child.text?.trim()))) {
      toast("Please fill in both title and description.", "#", "warning");
      return;
    }

    try {
      const db = getDatabase();
      const now = new Date();
      const day = String(now.getDate()).padStart(2, '0');
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const year = now.getFullYear();
      const hour = String(now.getHours()).padStart(2, '0');
      const minute = String(now.getMinutes()).padStart(2, '0');
      const CREATED_DATE = `${day}/${month}/${year} ${hour}:${minute}`;

      const annId = `ann_${Date.now()}`;
      
      await set(ref(db, `GROUPS/${groupId}/ANNOUNCEMENTS/${annId}`), {
        TITLE: announcementTitle,
        DESCRIPTION: JSON.stringify(announcementEditorValue),
        CREATED_BY: currentUser.uid,
        CREATED_DATE,
        ALLOW_COMMENTS: allowCommentsToggle,
      });

      setAnnouncementTitle("");
      setAnnouncementEditorValue([{ type: 'paragraph', children: [{ text: '' }] }]);
      setAllowCommentsToggle("Y");
      setShowNewAnnouncementForm(false);
      toast("Announcement posted!", "#", "success");
      fetchAnnouncements();
    } catch (error) {
      console.error("Error creating announcement:", error);
      toast("Error posting announcement. Please try again.", "#", "danger");
    }
  };

  // Update announcement
  const handleUpdateAnnouncement = async (annId) => {
    if (!announcementTitle.trim() || !announcementEditorValue.some(block => block.children?.some(child => child.text?.trim()))) {
      toast("Please fill in both title and description.", "#", "warning");
      return;
    }

    try {
      const db = getDatabase();
      await update(ref(db, `GROUPS/${groupId}/ANNOUNCEMENTS/${annId}`), {
        TITLE: announcementTitle,
        DESCRIPTION: JSON.stringify(announcementEditorValue),
        ALLOW_COMMENTS: allowCommentsToggle,
      });

      setAnnouncementTitle("");
      setAnnouncementEditorValue([{ type: 'paragraph', children: [{ text: '' }] }]);
      setAllowCommentsToggle("Y");
      setEditingAnnouncementId(null);
      setShowEditModal(false);
      toast("Announcement updated!", "#", "success");
      fetchAnnouncements();
    } catch (error) {
      console.error("Error updating announcement:", error);
      toast("Error updating announcement. Please try again.", "#", "danger");
    }
  };

  // Delete announcement - open confirmation modal
  const handleDeleteAnnouncement = (annId) => {
    setDeleteConfirmModalId(annId);
    setShowDeleteConfirmModal(true);
  };

  // Confirm delete announcement
  const confirmDeleteAnnouncement = async () => {
    if (!deleteConfirmModalId) return;

    try {
      const db = getDatabase();
      await set(ref(db, `GROUPS/${groupId}/ANNOUNCEMENTS/${deleteConfirmModalId}`), null);
      toast("Announcement deleted!", "#", "success");
      setShowDeleteConfirmModal(false);
      setDeleteConfirmModalId(null);
      fetchAnnouncements();
    } catch (error) {
      console.error("Error deleting announcement:", error);
      toast("Error deleting announcement. Please try again.", "#", "danger");
    }
  };

  // Add comment to announcement
  const handleAddComment = async (annId) => {
    const commentText = commentTexts[annId];
    if (!commentText || !commentText.trim()) {
      toast("Please enter a comment.", "#", "warning");
      return;
    }

    try {
      const db = getDatabase();
      const now = new Date();
      const day = now.getDate();
      const month = now.getMonth() + 1;
      const year = now.getFullYear();
      const hour = now.getHours();
      const CREATED_DATE = `${day}/${month}/${year} ${hour}`;

      const commentId = `comment_${Date.now()}`;
      
      await set(ref(db, `GROUPS/${groupId}/ANNOUNCEMENTS/${annId}/COMMENTS/${commentId}`), {
        TEXT: commentText,
        CREATED_BY: currentUser.uid,
        CREATED_DATE,
      });

      setCommentTexts({ ...commentTexts, [annId]: "" });
      fetchAnnouncements();
    } catch (error) {
      console.error("Error adding comment:", error);
      toast("Error adding comment. Please try again.", "#", "danger");
    }
  };

  // Add or update reaction
  const handleReaction = async (annId, reactionType) => {
    try {
      const db = getDatabase();
      
      // Check if user already has a reaction
      const reactionsRef = ref(db, `GROUPS/${groupId}/ANNOUNCEMENTS/${annId}/REACTIONS/${currentUser.uid}`);
      const snapshot = await get(reactionsRef);
      
      if (snapshot.exists() && snapshot.val() === reactionType) {
        // Remove reaction if clicking same one
        await set(reactionsRef, null);
      } else {
        // Add or update reaction
        await set(reactionsRef, reactionType);
      }
      
      fetchAnnouncements();
    } catch (error) {
      console.error("Error adding reaction:", error);
      toast("Error adding reaction. Please try again.", "#", "danger");
    }
  };

  // Get reaction counts
  const getReactionCount = (reactions, reactionType) => {
    if (!reactions) return 0;
    return Object.values(reactions).filter(r => r === reactionType).length;
  };

  // Check if user has reacted with this type
  const getUserReaction = (reactions) => {
    if (!reactions) return null;
    return reactions[currentUser?.uid] || null;
  };

  // Add or update comment reaction
  // const handleCommentReaction = async (annId, commentId, reactionType) => {
  //   try {
  //     const db = getDatabase();
      
  //     // Check if user already has a reaction
  //     const reactionsRef = ref(db, `GROUPS/${groupId}/ANNOUNCEMENTS/${annId}/COMMENTS/${commentId}/REACTIONS/${currentUser.uid}`);
  //     const snapshot = await get(reactionsRef);
      
  //     if (snapshot.exists() && snapshot.val() === reactionType) {
  //       // Remove reaction if clicking same one
  //       await set(reactionsRef, null);
  //     } else {
  //       // Add or update reaction
  //       await set(reactionsRef, reactionType);
  //     }
      
  //     fetchAnnouncements();
  //   } catch (error) {
  //     console.error("Error adding comment reaction:", error);
  //     toast("Error adding reaction. Please try again.", "#", "danger");
  //   }
  // };

  // Add reply to comment
  const handleAddReply = async (annId, commentId) => {
    const replyText = replyTexts[`${annId}-${commentId}`];
    if (!replyText || !replyText.trim()) {
      toast("Please enter a reply.", "#", "warning");
      return;
    }

    try {
      const db = getDatabase();
      const now = new Date();
      const day = now.getDate();
      const month = now.getMonth() + 1;
      const year = now.getFullYear();
      const hour = now.getHours();
      const CREATED_DATE = `${day}/${month}/${year} ${hour}`;

      const replyId = `reply_${Date.now()}`;
      
      await set(ref(db, `GROUPS/${groupId}/ANNOUNCEMENTS/${annId}/COMMENTS/${commentId}/REPLIES/${replyId}`), {
        TEXT: replyText,
        CREATED_BY: currentUser.uid,
        CREATED_DATE,
      });

      setReplyTexts({ ...replyTexts, [`${annId}-${commentId}`]: "" });
      fetchAnnouncements();
    } catch (error) {
      console.error("Error adding reply:", error);
      toast("Error adding reply. Please try again.", "#", "danger");
    }
  };

  // Fetch members when component loads and groupId is set
  useEffect(() => {
    fetchGroupMembers();
  }, [fetchGroupMembers]);

  // Fetch announcements when groupId changes
  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  // Initialize edit form when edit is toggled
  useEffect(() => {
    if (showEdit && group) {
      setEditGroupPublic(group.GROUP_PUBLIC || "Y");
      setEditGroupJoinOnly(group.GROUP_JOINONLY || "N");
      setEditGroupInstagram(group.GROUP_INSTAGRAM || "");
      setEditGroupYoutube(group.GROUP_YOUTUBE || "");
      setEditGroupTwitter(group.GROUP_TWITTER || "");
      setEditShowMemberCount(group.GROUP_SHOWMEMBERCOUNT || "Y");
      try {
        const parsed = JSON.parse(group.GROUP_DESC || '[]');
        setEditEditorValue(parsed.length > 0 ? parsed : [{ type: 'paragraph', children: [{ text: '' }] }]);
      } catch {
        setEditEditorValue([{ type: 'paragraph', children: [{ text: group.GROUP_DESC || '' }] }]);
      }
    }
  }, [showEdit, group]);

  // Fetch the group based on slug
  useEffect(() => {
    const fetchGroup = async () => {
      try {
        const db = getDatabase();
        const groupsRef = ref(db, "GROUPS");
        const snapshot = await get(groupsRef);

        if (snapshot.exists()) {
          const groupsData = snapshot.val();
          const foundEntry = Object.entries(groupsData).find(
            ([_, groupData]) => groupData.GROUP_SLUG === groupSlug
          );

          if (foundEntry) {
            const [id, foundGroup] = foundEntry;
            setGroup(foundGroup);
            setGroupId(id);

            // Check if group is public
            if (foundGroup.GROUP_PUBLIC === "N") {
            //   toast("This group is private.", "#", "danger");
            //   navigate("/", { replace: true });
              return;
            }
          } else {
            setGroup(null);
          }
        }
      } catch (error) {
        console.error("Error loading group:", error);
        toast("Error loading group", "#", "danger");
      } finally {
        setLoading(false);
      }
    };

    fetchGroup();
  }, [groupSlug, navigate]);

  // Check current user auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });

    return () => unsubscribe();
  }, []);

  // Check if current user is a member of the group
  useEffect(() => {
    if (!currentUser || !groupId) {
      setIsMember(false);
      return;
    }

    const checkMembership = async () => {
      try {
        const db = getDatabase();
        const memberRef = ref(db, `GROUPS/${groupId}/MEMBERS/${currentUser.uid}`);
        const snapshot = await get(memberRef);
        setIsMember(snapshot.exists());
      } catch (error) {
        console.error("Error checking membership:", error);
        setIsMember(false);
      }
    };

    checkMembership();
  }, [currentUser, groupId]);

  // Get current user's rank in the group
  useEffect(() => {
    if (!currentUser || !groupId || !isMember) {
      setUserRank(null);
      return;
    }

    const getUserRank = async () => {
      try {
        const db = getDatabase();
        const memberRef = ref(db, `GROUPS/${groupId}/MEMBERS/${currentUser.uid}`);
        const snapshot = await get(memberRef);
        if (snapshot.exists()) {
          setUserRank(snapshot.val().GROUP_RANK);
        }
      } catch (error) {
        console.error("Error getting user rank:", error);
        setUserRank(null);
      }
    };

    getUserRank();
  }, [currentUser, groupId, isMember]);

  // Fetch pending requests when user rank changes
  useEffect(() => {
    fetchPendingRequests();
  }, [fetchPendingRequests]);

  // Check if current user has requested to join a join-only group
  useEffect(() => {
    if (!currentUser || !groupId || !group?.GROUP_JOINONLY || group.GROUP_JOINONLY !== "Y") {
      setHasRequestedJoin(false);
      return;
    }

    const checkRequestStatus = async () => {
      try {
        const db = getDatabase();
        const whitelistRef = ref(db, `GROUPS/${groupId}/WHITELIST/${currentUser.uid}`);
        const snapshot = await get(whitelistRef);
        if (snapshot.exists()) {
          setHasRequestedJoin(true);
        } else {
          setHasRequestedJoin(false);
        }
      } catch (error) {
        console.error("Error checking request status:", error);
        setHasRequestedJoin(false);
      }
    };

    checkRequestStatus();
  }, [currentUser, groupId, group]);

  // Load group owner details
  useEffect(() => {
    const ownerId = group?.GROUP_OWNER;
    if (!ownerId) {
      setGroupOwner(null);
      return;
    }

    const db = getDatabase();
    const ownerRef = ref(db, `USERS/${ownerId}`);

    get(ownerRef)
      .then((snap) => {
        if (snap.exists()) {
          setGroupOwner({
            id: ownerId,
            ...snap.val(),
          });
        } else {
          setGroupOwner(null);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch group owner:", err);
        setGroupOwner(null);
      });
  }, [group]);

  // Fetch events linked to this group
  useEffect(() => {
    if (!groupId) {
      setGroupEvents([]);
      return;
    }

    const fetchGroupEvents = async () => {
      try {
        const db = getDatabase();
        const eventsRef = ref(db, `GROUPS/${groupId}/EVENTS`);
        const snapshot = await get(eventsRef);

        if (snapshot.exists()) {
          const eventsData = snapshot.val();
          const events = [];
          // console.log("Events data found:", eventsData);

          // Loop through EVENT_TYPE level (AUTOMOTIVE, SPORTS, MUSIC, etc)
          for (const eventType in eventsData) {
            // console.log(`Checking event type: ${eventType}`);
            const eventsByType = eventsData[eventType];
            // Loop through eventIds under each type - GROUPS only stores the ID reference
            for (const eventId in eventsByType) {
              // console.log(`Fetching event: ${eventId} from type: ${eventType}`);
              // Fetch the actual event data from main EVENTS collection
              // Path: EVENTS/EVENT/{eventType}/{eventId}
              const mainEventsRef = ref(db, `EVENTS/EVENT/${eventType}/${eventId}`);
              const eventSnapshot = await get(mainEventsRef);
              
              if (eventSnapshot.exists()) {
                // console.log(`Event found: ${eventId}`, eventSnapshot.val());
                events.push({
                  eventId,
                  groupCategory: eventType,  // Category from GROUPS path (for filtering)
                  ...eventSnapshot.val(), // Spread event data, including EVENT_TYPE from main collection
                });
              } else {
                // console.log(`Event NOT found at path: EVENTS/EVENT/${eventType}/${eventId}`);
              }
            }
          }

          // console.log("Final events array:", events);
          setGroupEvents(events);
        } else {
          // console.log("No events reference found in GROUPS");
          setGroupEvents([]);
        }
      } catch (error) {
        console.error("Error fetching group events:", error);
        setGroupEvents([]);
      }
    };

    fetchGroupEvents();
  }, [groupId]);

  useTitle(`- ${group ? group.GROUP_NAME : "Loading..."}`);

  const handleCopyUrl = async () => {
    try {
      const url = window.location.href;
      await navigator.clipboard.writeText(url);
      // toast("Copied URL. ", "#", "success");

          if (!url) return;


    } catch (err) {
      console.error("Failed to copy: ", err);
      // toast("Error copying URL. <br> Please Retry.", "#", "danger");
    }
  };


  if (loading)
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-default bg-opacity-80 z-50">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 text-primary border-t-transparent rounded-full animate-spin"></div>
          <h4 className="fw-bolder mb-3 mt-3">Loading...</h4>
        </div>
      </div>
    );

  if (!group) return (
    <div>
      <Navbar />
      <div className="container mt-5 mb-5">
        <p className="text-center py-5">Group not found.</p>
      </div>
      <Footer />
    </div>
  );

  // If group is join-only and user is not a member, show request join screen
  if (group.GROUP_JOINONLY === "Y" && !isMember) {
    return (
      <div>
        <Navbar />

        <div className="container mt-5 mb-5 ">

        <div className="row mb-4 align-items-center">
            <div className="col-6 text-start">
                <Link to="/Groups" data-discover="true">
                    <button className="text back-button"><i className="bi bi-arrow-left me-1 small"></i> Back</button>
                </Link>
            </div>
    
        </div>

        <div className={`event-image-fallback event-image- w-full object-cover max-sm:h-50 sm:aspect-[4/1]`}></div>
   
        <div className="row justify-content-between align-items-center desktop-sticky event-sub-navbar blap">

            <div className="col-md-8 col-sm-12">
            <h1 className="event-heading-start mb-1">{group.GROUP_NAME}</h1>

            <p className="mb-4">
                <span className="event-badges me-2">{group.GROUP_TYPE}</span>
                {group.GROUP_VERIFIED === "Y" && <span className="event-badges me-2"><i className="bi bi-check2-circle me-1 verified-badge"></i> Verified</span>}
            </p>
            
            <div id="description" className=" mb-4">
                {ledescription}
            </div>

            </div>


            {currentUser ? (
            <div className="col-md-4 col-sm-12 text-md-end text-sm-start mb-3s">

            {hasRequestedJoin ? (
                 <button
                    className="btn btn-primary"
                    >
                     Requested
                    </button>
              ) : (
                <button
                  className="btn btn-primary mb-3"
                  onClick={handleRequestJoin}
                  disabled={isJoining}
                >
                  {isJoining ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Sending Request...
                    </>
                  ) : (
                    <>
                     Request Join
                    </>
                  )}
                </button>
              )}
            </div>
          ) : (
            <button
              className="btn btn-primary"
              onClick={() => navigate("/Login")}
            >
            Sign In to Request Access
            </button>
          )}

        </div>

          <h4 className="event-sub-title my-4">This group requires approval to join</h4>
          <p className="event-text  mb-5">You need to request access to see this group's content.</p>
          

        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Navbar />

      {/* Header */}
      <div className="App-header mt-5 mb-5">
        <div className="container-md">

            <div className="row mb-4 align-items-center">

                <div className="col-6 text-start">
                    {currentUser && (currentUser.uid === group.GROUP_OWNER || userRank === "ADMIN") && (
                      <button 
                        className="btn-tertiary"
                        onClick={() => setShowEdit(!showEdit)}
                      >
                        <i className="bi bi-gear-wide me-1 small"></i> {showEdit ? 'Cancel' : 'Edit'}
                      </button>
                    )}
                </div>

                  <div className="col-6 text-end">
                    <button  target="_blank" rel="noopener noreferrer" className="btn-tertiary-outline " onClick={handleCopyUrl}>
                                <i className="bi bi-reply me-1 "></i>  Share
                     </button>
                  </div>
            </div>

    {!showEdit && (
    <> 

    {/* {imageSrc ? (
      <img
        className="w-full object-cover max-sm:h-50 sm:aspect-[4/1]"
        src={imageSrc}
        alt="event-banner"
        style={{
          viewTransitionName: `event-image-`,
          display: "block",
          width: "100%",
          borderRadius: "12px"
        }}
        loading="eager"
      />
    ) : ( */}
      <div className={`event-image-fallback event-image- w-full object-cover max-sm:h-50 sm:aspect-[4/1]`}></div>
    {/* )} */}


        <div className="row justify-content-between align-items-center desktop-sticky event-sub-navbar blap">

            <div className="col-md-8 col-sm-12">
            <h1 className="event-heading-start mb-1">{group.GROUP_NAME}</h1>

                <p className=" mb-4">
                <span className="event-badges me-2">{group.GROUP_TYPE}</span>

                  {(group.GROUP_INSTAGRAM?.trim() ||
                    group.GROUP_YOUTUBE?.trim() ||
                    group.GROUP_TWITTER?.trim()) && (
                    <>
                      {group.GROUP_INSTAGRAM?.trim() && (
                        <a
                          className="mb-0 event-badges me-2"
                          href={`https://www.instagram.com/${group.GROUP_INSTAGRAM.trim()}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <i className="bi bi-instagram me-2"></i>
                          {group.GROUP_INSTAGRAM.trim()}
                        </a>
                      )}

                      {group.GROUP_YOUTUBE?.trim() && (
                        <a
                          className="mb-0 event-badges me-2"
                          href={`https://www.youtube.com/${group.GROUP_YOUTUBE.trim()}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <i className="bi bi-youtube me-2"></i>
                          {group.GROUP_YOUTUBE.trim()}
                        </a>
                      )}

                      {group.GROUP_TWITTER?.trim() && (
                        <a
                          className="mb-0 event-badges me-2"
                          href={`https://www.twitter.com/${group.GROUP_TWITTER.trim()}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <i className="bi bi-twitter-x me-2"></i>
                          {group.GROUP_TWITTER.trim()}
                        </a>
                      )}
                    </>
                  )}

                {group.GROUP_SHOWMEMBERCOUNT === "Y" && (
                    <span className="mb-0 event-badges">
                      Members: {groupMembers.length}
                    </span>
                )} 

                {group.GROUP_VERIFIED === "Y" && <span className="event-badges me-2"><i className="bi bi-check2-circle me-1 verified-badge"></i> Verified</span>}

            </p>

                        
            <div id="description" className=" mb-4">
                {ledescription}
            </div>
            
            </div>
            

                {currentUser && currentUser.uid !== group.GROUP_OWNER && (
                    <div className="col-auto">
                    <button
                        className={`btn mb-3 ${isMember ? 'btn-primary' : 'btn-primary'}`}
                        onClick={isMember ? handleLeaveGroup : handleJoinGroup}
                        disabled={isJoining}
                    >
                        {isJoining ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {isMember ? 'Leaving...' : 'Joining...'}
                        </>
                        ) : (
                        isMember ? 'Leave Group' : 'Join Group'
                        )}
                    </button>
                    </div>
                )}

        </div>


              <nav>
                <div className="nav my-3" id="nav-tab" role="tablist">
                  <button className="nav-link active" id="nav-grouphome-tab" data-bs-toggle="tab" data-bs-target="#nav-grouphome" type="button" role="tab" aria-controls="nav-grouphome" aria-selected="true">Home</button>
                  <button className="nav-link" id="nav-groupattending-tab" data-bs-toggle="tab" data-bs-target="#nav-groupattending" type="button" role="tab" aria-controls="nav-groupattending" aria-selected="false">Attending</button>
                  {/* <button className="nav-link" id="nav-groupdiscussion-tab" data-bs-toggle="tab" data-bs-target="#nav-groupdiscussion" type="button" role="tab" aria-controls="nav-groupdiscussion" aria-selected="false">Discussion</button> */}
                </div>
              </nav>

              <div className="tab-content" id="nav-tabContent">

              <div className="tab-pane fade show active" id="nav-grouphome" role="tabpanel" aria-labelledby="nav-grouphome-tab"> 

                <div className="row mt-4 mb-5">
                  {/* Main Content */}
                  <div className="col-md-12 col-sm-12">

                    {/* Announcements Section */}
                    <div className="mb-5">
                      <div className="row justify-content-between align-items-center mb-3">
                        <div className="col-auto">
                          <h3 className="mb-0">Announcements</h3>
                          <p className="text-secondary mb-0"> All Group updates all in one place.</p>
                        </div>
                        {/* Create Announcement Button - Admins Only */}
                        {currentUser && (currentUser.uid === group.GROUP_OWNER || userRank === "ADMIN") && (
                          <div className="col-auto">
                            <button 
                              className="btn btn-primary mb-4"
                              onClick={() => {
                                if (editingAnnouncementId) {
                                  setEditingAnnouncementId(null);
                                  setAnnouncementTitle("");
                                  setAnnouncementEditorValue([{ type: 'paragraph', children: [{ text: '' }] }]);
                                  setAllowCommentsToggle("Y");
                                }
                                setShowNewAnnouncementForm(!showNewAnnouncementForm);
                              }}
                            >
                            {showNewAnnouncementForm ? 'Cancel' : 'New Post'}
                            </button>
                          </div>
                        )}
                      </div>

                      {/* New Announcement Form - Always Create */}
                      {showNewAnnouncementForm && currentUser && (currentUser.uid === group.GROUP_OWNER || userRank === "ADMIN") && (
                        <div className="mb-4 rounded group-announcement-card">
                          <div className="p-2">
                            <h5 className="mb-3 fw-600">Create New Announcement</h5>
                            <div className="mb-3">
                              <label className="form-label fw-500 mb-2">Title</label>
                              <input 
                                type="text" 
                                className="form-control" 
                                value={announcementTitle}
                                onChange={(e) => setAnnouncementTitle(e.target.value)}
                                placeholder="Enter announcement title"
                                style={{ borderRadius: "6px", borderColor: "#e5e7eb" }}
                              />
                            </div>
                            <div className="mb-3">
                              <label className="form-label fw-500 mb-2">Description</label>
                              <div className="editor-wrapper">
                                <div className="editor-toolbar" style={{ borderRadius: "6px 6px 0 0" }}>
                                  <AnnMarkButton format="bold" label='<i class="bi bi-type-bold"></i>' />
                                  <AnnMarkButton format="italic" label='<i class="bi bi-type-italic"></i>' />
                                  <AnnMarkButton format="underline" label='<i class="bi bi-type-underline"></i>' />
                                  <AnnBlockButton format="heading-one" label='<i class="bi bi-type-h1"></i>' />
                                  <AnnBlockButton format="heading-two" label='<i class="bi bi-type-h2"></i>' />
                                  <AnnBlockButton format="heading-three" label='<i class="bi bi-type-h3"></i>' />
                                  <AnnBlockButton format="paragraph" label='<i class="bi bi-paragraph"></i>' />
                                  <AnnBlockButton format="bulleted-list" label='<i class="bi bi-list-task"></i>' />
                                  <AnnBlockButton format="numbered-list" label='<i class="bi bi-list-ol"></i>' />
                                  <AnnYoutubeButton />
                                </div>
                                <div className="editor-container" style={{ borderRadius: "0 0 6px 6px" }}>
                                  <Slate key={`slate-ann-create`} editor={announcementEditorRef.current} initialValue={announcementEditorValue && announcementEditorValue.length > 0 ? announcementEditorValue : [{ type: 'paragraph', children: [{ text: '' }] }]} onChange={setAnnouncementEditorValue}>
                                   <Editable 
                                      placeholder="Type your announcement description here..."
                                      style={{ outline: 'none', minHeight: '140px', fontFamily: 'inherit', fontSize: 'inherit', padding: '12px' }}
                                      renderElement={({ attributes, children, element }) => {
                                        switch (element.type) {
                                          case 'heading-one': return <h1 {...attributes} style={{ fontSize: '24px', fontWeight: 'bold', margin: '15px 0 10px 0' }}>{children}</h1>;
                                          case 'heading-two': return <h2 {...attributes} style={{ fontSize: '20px', fontWeight: 'bold', margin: '12px 0 8px 0' }}>{children}</h2>;
                                          case 'heading-three': return <h3 {...attributes} style={{ fontSize: '16px', fontWeight: 'bold', margin: '10px 0 6px 0' }}>{children}</h3>;
                                          case 'bulleted-list': return <li {...attributes} style={{ marginLeft: '20px', listStyleType: 'disc' }}>{children}</li>;
                                          case 'numbered-list': return <li {...attributes} style={{ marginLeft: '20px', listStyleType: 'decimal' }}>{children}</li>;
                                          case 'youtube': return <div {...attributes} style={{ margin: '16px 0', borderRadius: '8px', overflow: 'hidden', position: 'relative' }}><iframe width="100%" height="615px" src={`https://www.youtube.com/embed/${element.videoId}`} title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ maxWidth: '100%', maxHeight: '615px' }}></iframe><div contentEditable={false} style={{ textAlign: 'center', fontSize: '0.85rem', color: '#666', marginTop: '8px' }}>YouTube Video</div></div>;
                                          default: return <p {...attributes}>{children}</p>;
                                        }
                                      }}
                                      renderLeaf={({ attributes, children, leaf }) => {
                                        let newChildren = children;
                                        if (leaf.bold) newChildren = <strong>{newChildren}</strong>;
                                        if (leaf.italic) newChildren = <em>{newChildren}</em>;
                                        if (leaf.underline) newChildren = <u>{newChildren}</u>;
                                        return <span {...attributes}>{newChildren}</span>;
                                      }}
                                    />
                                  </Slate>
                                </div>
                              </div>
                            </div>
                            <div className="mb-3">
                              <label htmlFor="allow-comments" className="form-label fw-500 mb-2">Allow Comments</label>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="allow-comments"
                                  checked={allowCommentsToggle === "Y"}
                                  onChange={(e) => setAllowCommentsToggle(e.target.checked ? "Y" : "N")}
                                />
                                <label className="form-check-label" htmlFor="allow-comments">
                                  {allowCommentsToggle === "Y" ? "Yes" : "No"}
                                </label>
                              </div>
                            </div>
                            <div className="d-flex justify-content-end gap-2 mt-4">
                              <button 
                                className="btn btn-outline-secondary"
                                onClick={() => {
                                  setShowNewAnnouncementForm(false);
                                  setAnnouncementTitle("");
                                  setAnnouncementEditorValue([{ type: 'paragraph', children: [{ text: '' }] }]);
                                  setAllowCommentsToggle("Y");
                                }}
                                style={{ borderRadius: "6px" }}
                              >
                                <i className="bi bi-x me-2"></i>Cancel
                              </button>
                              <button 
                                className="btn btn-primary"
                                onClick={() => {
                                  handleCreateAnnouncement();
                                }}
                                style={{ borderRadius: "6px" }}
                              >
                                Post
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Announcements List */}
                      {announcements.length > 0 ? (
                        <div>
                          {announcements.map((ann) => (
                            <div key={ann.id} className="mb-4 rounded group-announcement-card">
                              <div className="p-4">
                                {/* Header with Title and Actions */}
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                  <div className="flex-grow-1">
                                    <h4 className="mb-2 bold">{ann.TITLE}</h4>
                                    <small>
                                      <i className="bi bi-person-circle"></i>
                                      <span className="fw-500">{ann.creatorData?.USERDISPLAYNAME || "Unknown"}</span>
                                      - <i className="bi bi-calendar2-week"></i> at {" "} {ann.CREATED_DATE}
                                    </small>
                                  </div>
                                  
                                  {/* Edit/Delete Buttons - Admins Only */}
                                  {currentUser && (currentUser.uid === group.GROUP_OWNER || userRank === "ADMIN") && (
                                    <div className="ms-3 d-flex gap-2">
                                      <button 
                                        className="btn-tertiary-outline"
                                        onClick={() => {
                                          setEditingAnnouncementId(ann.id);
                                          setAnnouncementTitle(ann.TITLE);
                                          setAllowCommentsToggle(ann.ALLOW_COMMENTS || "Y");
                                          try {
                                            const parsed = JSON.parse(ann.DESCRIPTION);
                                            setAnnouncementEditorValue(parsed.length > 0 ? parsed : [{ type: 'paragraph', children: [{ text: '' }] }]);
                                          } catch {
                                            setAnnouncementEditorValue([{ type: 'paragraph', children: [{ text: ann.DESCRIPTION || '' }] }]);
                                          }
                                          setShowEditModal(true);
                                        }}
                                        title="Edit announcement"
                                      >
                                        <i className="bi bi-pencil"></i> Edit
                                      </button>
                                      <button 
                                        className="btn  btn-outline-danger"
                                        onClick={() => handleDeleteAnnouncement(ann.id)}
                                        title="Delete announcement"
                                      >
                                        <i className="bi bi-trash3"></i> Delete
                                      </button>
                                    </div>
                                  )}
                                </div>

                                {/* Description */}
                                <div className="mb-4 ann-description">
                                  {convertSlateToJSXWithYoutube(ann.DESCRIPTION) || ann.DESCRIPTION}
                                </div>

                                {/* Reactions */}
                                <div className="group-reactions">
                                  {[
                                    { key: 'thumbs-up', icon: 'bi-hand-thumbs-up', title: 'Like' },
                                    { key: 'thumbs-down', icon: 'bi-hand-thumbs-down', title: 'Dislike' },
                                    { key: 'laugh', icon: 'bi-emoji-laughing', title: 'Laugh' },
                                    { key: 'angry', icon: 'bi-emoji-angry-fill', title: 'Angry' },
                                    { key: 'heart', icon: 'bi-heart-fill', title: 'Love' }
                                  ].map((reaction) => {
                                    const count = getReactionCount(ann.REACTIONS, reaction.key);
                                    const userReaction = getUserReaction(ann.REACTIONS);
                                    const isUserReacted = userReaction === reaction.key;
                                    
                                    return (
                                      <button
                                        key={reaction.key}
                                        className={`btn btn-sm ${isUserReacted ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => handleReaction(ann.id, reaction.key)}
                                        title={reaction.title}
                                      >
                                        <i className={`bi ${reaction.icon}`}></i>
                                        {count > 0 && <span className="ms-1">{count}</span>}
                                      </button>
                                    );
                                  })}
                                </div>

                                {/* Comments Toggle Section - Only show if comments are allowed */}
                                {ann.ALLOW_COMMENTS !== "N" && (
                                <div className="border-top mt-4 pt-4">
                                  <button
                                    className="btn btn-sm text-decoration-none fw-500 d-flex align-items-center text-muted"
                                    onClick={() => setExpandedComments({ ...expandedComments, [ann.id]: !expandedComments[ann.id] })}
                                  >
                                    <i className={`bi ${expandedComments[ann.id] ? 'bi-chevron-up' : 'bi-chevron-down'} me-2`}></i>
                                    Comments
                                    <span className="badge bg-light text-dark ms-2">{ann.COMMENTS ? Object.keys(ann.COMMENTS).length : 0}</span>
                                  </button>

                                  {/* Expanded Comments Section */}
                                  <div className="group-comments-holder">
                                  {expandedComments[ann.id] && (
                                    <div className="my-2">
                                      {/* Comments List */}
                                      {ann.COMMENTS && Object.keys(ann.COMMENTS).length > 0 ? (
                                        <div className="">
                                          {Object.entries(ann.COMMENTS).map(([commentId, comment]) => (
                                            <div key={commentId} className="mb-2 p-2">
                                              {/* Comment Header */}
                                              <div className="d-flex justify-content-between align-items-start mb-2">
                                                <strong style={{ fontSize: "0.95rem" }}>{comment.creatorData?.USERDISPLAYNAME || "Unknown User"}</strong>
                                                <small style={{ opacity: 0.6 }}>{comment.CREATED_DATE}</small>
                                              </div>
                                              
                                              {/* Comment Text */}
                                              <p className="mb-3" style={{ fontSize: "0.9rem", lineHeight: "1.5" }}>{comment.TEXT}</p>

                                              {/* Comment Reactions */}
                                              {/* <div className="mb-2 group-reactions">
                                                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                                                  {[
                                                    { key: 'thumbs-up', icon: 'bi-hand-thumbs-up', title: 'Like' },
                                                    { key: 'thumbs-down', icon: 'bi-hand-thumbs-down', title: 'Dislike' },
                                                    { key: 'laugh', icon: 'bi-emoji-laughing', title: 'Laugh' },
                                                    { key: 'angry', icon: 'bi-emoji-angry-fill', title: 'Angry' },
                                                    { key: 'heart', icon: 'bi-heart-fill', title: 'Love' }
                                                  ].map((reaction) => {
                                                    const count = getReactionCount(comment.REACTIONS, reaction.key);
                                                    const userReaction = getUserReaction(comment.REACTIONS);
                                                    const isUserReacted = userReaction === reaction.key;
                                                    
                                                    return (
                                                      <button
                                                        key={reaction.key}
                                                        className={`btn btn-sm ${isUserReacted ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                        onClick={() => handleCommentReaction(ann.id, commentId, reaction.key)}
                                                        title={reaction.title}
                                                        style={{ fontSize: "0.8rem" }}
                                                      >
                                                        <i className={`bi ${reaction.icon}`}></i>
                                                        {count > 0 && <span className="ms-1">{count}</span>}
                                                      </button>
                                                    );
                                                  })}
                                                </div>
                                              </div> */}

                                              {/* Replies Toggle */}
                                              {comment.REPLIES && Object.keys(comment.REPLIES).length > 0 && (
                                              <div className="border-top pt-2 mt-2">
                                                <button
                                                  className="btn  text-decoration-none fw-500 d-flex align-items-center text-muted"
                                                  onClick={() => setExpandedReplies({ ...expandedReplies, [`${ann.id}-${commentId}`]: !expandedReplies[`${ann.id}-${commentId}`] })}
                                                  style={{ fontSize: "0.85rem" }}
                                                >
                                                  <i className={`bi ${expandedReplies[`${ann.id}-${commentId}`] ? 'bi-chevron-up' : 'bi-chevron-down'} me-2`}></i>
                                                  Replies
                                                  <span className="badge bg-light text-dark ms-2">{Object.keys(comment.REPLIES).length}</span>
                                                </button>

                                                {/* Expanded Replies Section */}
                                                {expandedReplies[`${ann.id}-${commentId}`] && (
                                                  <div className="mt-2">
                                                    {/* Replies List */}
                                                    {comment.REPLIES && Object.keys(comment.REPLIES).length > 0 ? (
                                                      <div className="mb-2">
                                                        {Object.entries(comment.REPLIES).map(([replyId, reply]) => (
                                                          <div key={replyId} className=" p-2 border-top">
                                                            <div className="d-flex justify-content-between align-items-middle align-middle mb-1">
                                                              <div> 
                                                               <strong>{reply.creatorData?.USERDISPLAYNAME || "Unknown User"}</strong>
                                                              - 
                                                                <i className="bi bi-calendar2-week me-1"></i><span>{reply.CREATED_DATE}</span>
                                                              </div>
                                                            </div>
                                                            <p className="mb-0 bg-light p-2" style={{ fontSize: "0.85rem", lineHeight: "1.4" }}>{reply.TEXT}</p>
                                                          </div>
                                                        ))}
                                                      </div>
                                                    ) : (
                                                      <></>
                                                    )}

                                                    {/* Add Reply Form */}
                                                    {currentUser && isMember && (
                                                      <div className="d-flex gap-2">
                                                        <input 
                                                          type="text" 
                                                          className="form-control"
                                                          placeholder="Add a reply..."
                                                          value={replyTexts[`${ann.id}-${commentId}`] || ""}
                                                          onChange={(e) => setReplyTexts({ ...replyTexts, [`${ann.id}-${commentId}`]: e.target.value })}
                                                          style={{ borderRadius: "6px", fontSize: "0.85rem" }}
                                                        />
                                                        <button 
                                                          className="btn btn-sm btn-primary"
                                                          onClick={() => handleAddReply(ann.id, commentId)}
                                                          style={{ borderRadius: "6px" }}
                                                        >
                                                          Reply
                                                        </button>
                                                      </div>
                                                    )}
                                                  </div>
                                                )}
                                              </div>
                                              )}

                                              {/* Add Reply Form when no replies exist */}
                                              {(!comment.REPLIES || Object.keys(comment.REPLIES).length === 0) && currentUser && isMember && (
                                              <div className="border-top pt-2 mt-2">
                                                <div className="d-flex gap-2">
                                                  <input 
                                                    type="text" 
                                                    className="form-control"
                                                    placeholder="Add a reply..."
                                                    value={replyTexts[`${ann.id}-${commentId}`] || ""}
                                                    onChange={(e) => setReplyTexts({ ...replyTexts, [`${ann.id}-${commentId}`]: e.target.value })}
                                                    style={{ borderRadius: "6px", fontSize: "0.85rem" }}
                                                  />
                                                  <button 
                                                    className="btn btn-sm btn-primary"
                                                    onClick={() => handleAddReply(ann.id, commentId)}
                                                    style={{ borderRadius: "6px" }}
                                                  >
                                                    Reply
                                                  </button>
                                                </div>
                                              </div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <></>
                                      )}

                                      {/* Add Comment Form */}
                                      {currentUser && isMember && ann.ALLOW_COMMENTS !== "N" && (
                                        <div className="d-flex gap-2">
                                          <input 
                                            type="text" 
                                            className="form-control"
                                            placeholder="..."
                                            value={commentTexts[ann.id] || ""}
                                            onChange={(e) => setCommentTexts({ ...commentTexts, [ann.id]: e.target.value })}
                                            style={{ borderRadius: "6px", fontSize: "0.9rem" }}
                                          />
                                          <button 
                                            className="btn btn-sm btn-primary"
                                            onClick={() => handleAddComment(ann.id)}
                                            style={{ borderRadius: "6px" }}
                                          >
                                            Send
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  </div>
                                </div>
                                )}

                                {/* Comments Disabled Message */}
                                {ann.ALLOW_COMMENTS === "N" && (
                                <div className="border-top mt-4 pt-4">
                                  <p style={{ fontSize: "0.9rem", opacity: 0.6 }} className="mb-0">
                                    <i className="bi bi-ban me-2"></i>Comments are disabled for this post.
                                  </p>
                                </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted text-center py-4">No announcements yet. Check back soon!</p>
                      )}

                      {/* Delete Confirmation Modal */}
                      {showDeleteConfirmModal && deleteConfirmModalId && (
                        <div style={{
                          position: 'fixed',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 2000
                        }}>
                          <div style={{
                            backgroundColor: 'var(--card-bg)',
                            borderRadius: '12px',
                            padding: '28px',
                            maxWidth: '400px',
                            width: '90%',
                            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
                            animation: 'fadeIn 0.2s ease-out'
                          }}>
                            <div className="text-center">
                              <i className="bi bi-exclamation-circle" style={{ fontSize: '3rem', color: 'var(--color-secondary)', marginBottom: '1rem', display: 'block' }}></i>
                              <h5 style={{ color: 'var(--text)', marginBottom: '0.5rem', fontWeight: '700' }}>Delete Announcement?</h5>
                              <p style={{ color: 'var(--event-text)', marginBottom: '1.5rem', lineHeight: '1.6' }}>This action cannot be undone. The announcement and all its comments will be permanently deleted.</p>
                              <div className="d-flex gap-2 justify-content-center">
                                <button 
                                  className="btn text-white"
                                  onClick={() => {
                                    setShowDeleteConfirmModal(false);
                                    setDeleteConfirmModalId(null);
                                  }}
                                  style={{ borderRadius: '7px', fontWeight: '600' }}
                                >
                                 Cancel
                                </button>
                                <button 
                                  className="btn btn-outline-danger"
                                  onClick={confirmDeleteAnnouncement}
                                  style={{ borderRadius: '7px', fontWeight: '600' }}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Edit Announcement Modal Overlay */}
                      {showEditModal && editingAnnouncementId && currentUser && (currentUser.uid === group.GROUP_OWNER || userRank === "ADMIN") && (
                        <div style={{
                          position: 'fixed',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 1000
                        }}>
                          <div style={{
                            backgroundColor: 'var(--card-bg)',
                            borderRadius: '8px',
                            padding: '20px',
                            maxWidth: '600px',
                            width: '90%',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                          }}>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h5 style={{ color: 'var(--text)' }} className="mb-0">Edit Announcement</h5>
                              <button 
                                className="btn btn-close" 
                                onClick={() => {
                                  setShowEditModal(false);
                                  setEditingAnnouncementId(null);
                                  setAnnouncementTitle("");
                                  setAnnouncementEditorValue([{ type: 'paragraph', children: [{ text: '' }] }]);
                                  setAllowCommentsToggle("Y");
                                }}
                              ></button>
                            </div>

                            <div className="mb-3">
                              <label className="form-label">Title</label>
                              <input 
                                type="text" 
                                className="form-control" 
                                value={announcementTitle}
                                onChange={(e) => setAnnouncementTitle(e.target.value)}
                                placeholder="Announcement title"
                              />
                            </div>

                            <div className="mb-3">
                              <label className="form-label">Description</label>
                              <div className="editor-wrapper">
                                <div className="editor-toolbar">
                                  <AnnMarkButton format="bold" label='<i class="bi bi-type-bold"></i>' />
                                  <AnnMarkButton format="italic" label='<i class="bi bi-type-italic"></i>' />
                                  <AnnMarkButton format="underline" label='<i class="bi bi-type-underline"></i>' />
                                  <AnnBlockButton format="heading-one" label='<i class="bi bi-type-h1"></i>' />
                                  <AnnBlockButton format="heading-two" label='<i class="bi bi-type-h2"></i>' />
                                  <AnnBlockButton format="heading-three" label='<i class="bi bi-type-h3"></i>' />
                                  <AnnBlockButton format="paragraph" label='<i class="bi bi-paragraph"></i>' />
                                  <AnnBlockButton format="bulleted-list" label='<i class="bi bi-list-task"></i>' />
                                  <AnnBlockButton format="numbered-list" label='<i class="bi bi-list-ol"></i>' />
                                  <AnnYoutubeButton />
                                </div>
                                <div className="editor-container">
                                  <Slate key={`slate-ann-edit-${editingAnnouncementId}`} editor={announcementEditorRef.current} initialValue={announcementEditorValue && announcementEditorValue.length > 0 ? announcementEditorValue : [{ type: 'paragraph', children: [{ text: '' }] }]} onChange={setAnnouncementEditorValue}>
                                   <Editable 
                                      placeholder="Type your announcement description here..."
                                      style={{ outline: 'none', minHeight: '120px', fontFamily: 'inherit', fontSize: 'inherit' }}
                                      renderElement={({ attributes, children, element }) => {
                                        switch (element.type) {
                                          case 'heading-one': return <h1 {...attributes} style={{ fontSize: '24px', fontWeight: 'bold', margin: '15px 0 10px 0' }}>{children}</h1>;
                                          case 'heading-two': return <h2 {...attributes} style={{ fontSize: '20px', fontWeight: 'bold', margin: '12px 0 8px 0' }}>{children}</h2>;
                                          case 'heading-three': return <h3 {...attributes} style={{ fontSize: '16px', fontWeight: 'bold', margin: '10px 0 6px 0' }}>{children}</h3>;
                                          case 'bulleted-list': return <li {...attributes} style={{ marginLeft: '20px', listStyleType: 'disc' }}>{children}</li>;
                                          case 'numbered-list': return <li {...attributes} style={{ marginLeft: '20px', listStyleType: 'decimal' }}>{children}</li>;
                                          case 'youtube': return <div {...attributes} style={{ margin: '16px 0', borderRadius: '8px', overflow: 'hidden' }}><iframe width="100%" height="auto" src={`https://www.youtube.com/embed/${element.videoId}`} title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ maxWidth: '100%' }}></iframe><div contentEditable={false} style={{ textAlign: 'center', fontSize: '0.85rem', color: '#666', marginTop: '8px' }}>YouTube Video</div></div>;
                                          default: return <p {...attributes}>{children}</p>;
                                        }
                                      }}
                                      renderLeaf={({ attributes, children, leaf }) => {
                                        let newChildren = children;
                                        if (leaf.bold) newChildren = <strong>{newChildren}</strong>;
                                        if (leaf.italic) newChildren = <em>{newChildren}</em>;
                                        if (leaf.underline) newChildren = <u>{newChildren}</u>;
                                        return <span {...attributes}>{newChildren}</span>;
                                      }}
                                    />
                                  </Slate>
                                </div>
                              </div>
                            </div>

                            <div className="mb-3">
                              <label htmlFor="edit-allow-comments" className="form-label fw-500 mb-2">Allow Comments</label>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="edit-allow-comments"
                                  checked={allowCommentsToggle === "Y"}
                                  onChange={(e) => setAllowCommentsToggle(e.target.checked ? "Y" : "N")}
                                />
                                <label className="form-check-label" htmlFor="edit-allow-comments">
                                  {allowCommentsToggle === "Y" ? "Yes" : "No"}
                                </label>
                              </div>
                            </div>

                            <div className="d-flex justify-content-between gap-2">
                              <button 
                                className="btn btn-outline-danger"
                                onClick={() => {
                                  setShowEditModal(false);
                                  setEditingAnnouncementId(null);
                                  setAnnouncementTitle("");
                                  setAnnouncementEditorValue([{ type: 'paragraph', children: [{ text: '' }] }]);
                                  setAllowCommentsToggle("Y");
                                }}
                              >
                                Cancel
                              </button>
                              <button 
                                className="btn btn-primary"
                                onClick={() => {
                                  handleUpdateAnnouncement(editingAnnouncementId);
                                }}
                              >
                                Update
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>

              <div className="tab-pane fade " id="nav-groupattending" role="tabpanel" aria-labelledby="nav-groupattending-tab"> 

                <div className="row mt-4 mb-5">
                  <div className="col-md-12 col-sm-12">
                
                    {/* Events Section */}
                    {groupEvents.length > 0 && (
                      <div className="mb-4">
                        <h5 className="event-sub-title mb-3">We're going to...</h5>
                        
                        {/* Category Selector */}
                        <div className="row"> 
                          <div className="col-auto">
                          <div className="mb-4">
                            <select
                              className="form-select"
                              value={selectedCategory}
                              onChange={(e) => setSelectedCategory(e.target.value)}
                            >
                              <option value="AUTOMOTIVE">AUTOMOTIVE</option>
                              <option value="SPORTS">SPORTS</option>
                              <option value="MUSIC">MUSIC</option>
                            </select>
                          </div>
                          </div>
                        </div>

                        {/* Calendar View */}
                        {calendarEvents.length > 0 ? (
                          <div className="mb-4 group-calendar-container">
                            <div className="collapsed show mb-3" id="groupCalendarView">
                              <FullCalendar
                                ref={calendarRef}
                                plugins={[dayGridPlugin]}
                                initialView={calendarView}
                                events={calendarEvents}
                                eventClassNames={(event) => `attending-cal-${event.event.extendedProps.type}`}
                                eventClick={(info) => {
                                  const slug = info.event.extendedProps.slug;
                                  const urlkey = info.event.extendedProps.urlkey;
                                  if (slug && urlkey) {
                                    navigate(`/event/${urlkey}/${slug}`);
                                  }
                                }}
                                eventDidMount={(info) => {
                                  const bgImage = info.event.extendedProps.backgroundImage;
                                  const eventType = info.event.extendedProps.type;
                                  
                                  // Color mapping for borders
                                  const typeColorMap = {
                                    'RALLY': '#e4d270',
                                    'RACE': '#79c3e3',
                                    'TRACKDAY': '#FD769A',
                                    'DRAG': '#D0FD98',
                                    'MEETUP': '#ad9fec',
                                    'DRIFT': '#98FDB6',
                                    'OTHER': '#FABCB4',
                                    'MOTORBIKE': '#d2dce1',
                                    'HOCKEY': '#e4d270',
                                    'FOOTBALL': '#79c3e3',
                                    'BASKETBALL': '#FD769A',
                                    'SWIMMING': '#D0FD98',
                                  };
                                  
                                  // Find the day cell
                                  const dayCell = info.el.closest('.fc-daygrid-day');
                                  
                                  if (dayCell) {
                                    // Check if we already applied a background (multiple events on same day)
                                    if (!dayCell.dataset.backgroundApplied) {
                                      dayCell.dataset.backgroundApplied = 'true';
                                      
                                      if (bgImage) {
                                        // Apply background image with darkening filter
                                        dayCell.style.backgroundImage = `url('${bgImage}')`;
                                        dayCell.style.backgroundSize = 'cover';
                                        dayCell.style.backgroundPosition = 'center';
                                        dayCell.style.backgroundRepeat = 'no-repeat';
                                        dayCell.style.backgroundAttachment = 'scroll';
                                        dayCell.style.filter = 'brightness(0.90)';
                                        dayCell.style.borderRadius = '8px';
                                      } else {
                                        // Apply pattern class to day cell
                                        dayCell.classList.add(`calendar-event-image-${eventType}`);
                                        dayCell.style.borderRadius = '8px';
                                      }
                                    }
                                    
                                    // Add border and style day number
                                    const borderColor = typeColorMap[eventType] || '#999';
                                    const isMobile = window.innerWidth <= 768;
                                    const borderWidth = isMobile ? '2px' : '3px';
                                    dayCell.style.border = `${borderWidth} solid ${borderColor} !important`;
                                    dayCell.style.boxSizing = 'border-box';
                                    
                                    // Style the day number with background color and white text
                                    const dayNumber = dayCell.querySelector('.fc-daygrid-day-number');
                                    if (dayNumber) {
                                      dayNumber.style.backgroundColor = borderColor;
                                      dayNumber.style.color = '#fff';
                                      dayNumber.style.fontWeight = 'bold';
                                      const padding = isMobile ? '2px 4px' : '4px 8px';
                                      dayNumber.style.padding = padding;
                                      dayNumber.style.borderRadius = '4px';
                                      dayNumber.style.display = 'inline-block';
                                      dayNumber.style.fontSize = isMobile ? '0.8rem' : 'inherit';
                                    }
                                  }
                                  
                                  // Make event itself transparent so day background shows through
                                  info.el.style.backgroundColor = 'transparent';
                                  info.el.style.border = 'none';
                                  info.el.style.cursor = 'pointer';
                                  
                                  // Style the event title
                                  const titleEl = info.el.querySelector('.fc-event-title');
                                  if (titleEl) {
                                    const isMobile = window.innerWidth <= 768;
                                    if (bgImage) {
                                      titleEl.style.color = '';
                                      titleEl.style.textShadow = '0 0 3px rgba(0,0,0,0.8)';
                                    } else {
                                      titleEl.style.color = '';
                                    }
                                    titleEl.style.fontWeight = '800';
                                    titleEl.style.fontFamily = 'Switzer';
                                    titleEl.style.whiteSpace = 'initial';
                                    titleEl.style.fontSize = isMobile ? '0.7rem' : 'inherit';
                                    titleEl.style.lineHeight = '1.2';
                                    titleEl.style.overflow = 'hidden';
                                    titleEl.style.textOverflow = 'ellipsis';
                                    titleEl.style.display = '-webkit-box';
                                    titleEl.style.webkitLineClamp = '2';
                                    titleEl.style.webkitBoxOrient = 'vertical';
                                  }
    
                                  // Style event content wrapper for readability
                                  const eventContent = info.el.querySelector('.fc-event-title-container');
                                  if (eventContent && bgImage) {
                                    const isMobile = window.innerWidth <= 768;
                                    eventContent.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';
                                    eventContent.style.padding = isMobile ? '2px 3px' : '4px 6px';
                                    eventContent.style.borderRadius = '3px';
                                  }
                                }}
                                headerToolbar={{
                                  left: "title",
                                  center: "",
                                  right: "prev,next",
                                }}
                              />
                            </div>
                          </div>
                        ) : (
                          <div style={{ width: '100%' }}>
                            <p className="my-3 mb-5">We're currently not attending any {selectedCategory.toLowerCase()} events.</p>
                          </div>
                        )}
                      </div>
                    )}

                  </div>
                </div>

              </div>

              <div className="tab-pane fade show active" id="nav-groupdiscussion" role="tabpanel" aria-labelledby="nav-groupdiscussion-tab"> 
              </div>

            </div>
        </>
    )}

  {/* EDIT SECTION */}
        {showEdit && currentUser && (currentUser.uid === group.GROUP_OWNER || userRank === "ADMIN") && (
          <div className="mt-4 mb-5">
            <div className="card-body">
              <h4 className="fw-bolder mb-4">Edit Group</h4>

              <nav>
                <div className="nav nav-tabs" id="nav-tab" role="tablist">
                  <button className="nav-link active" id="nav-general-tab" data-bs-toggle="tab" data-bs-target="#nav-general" type="button" role="tab" aria-controls="nav-general" aria-selected="true">General Settings</button>
                  <button className="nav-link" id="nav-members-tab" data-bs-toggle="tab" data-bs-target="#nav-members" type="button" role="tab" aria-controls="nav-members" aria-selected="false">Members <span className="">- {groupMembers.length}</span></button>
                  <button className="nav-link" id="nav-requests-tab" data-bs-toggle="tab" data-bs-target="#nav-requests" type="button" role="tab" aria-controls="nav-requests" aria-selected="false">Requests  <span className="">-  {pendingRequests.length} </span></button>
                </div>
              </nav>
              <div className="tab-content" id="nav-tabContent">

                {/* General Settings Tab */}
                <div className="tab-pane fade show active" id="nav-general" role="tabpanel" aria-labelledby="nav-general-tab">
                  <div className="mt-5">

                    <div className="row justify-content-between align-items-end m-3 mt-4 mb-0">
                      <div className="col-sm-12 "><h2>Edit General Group Settings</h2>
                        <p className="card-text text-gray-500 ">Edit the general settings of your group, including its name, description (Contact Support for custom banner image, more to come soon).</p>
                      </div>
                    </div>

                    <div className="filter-section"></div>

                    {/* Description */}
                    <div className="row mb-3">
                      <div className="col-12 mb-3">
                        <label className="form-label">
                          Description
                        </label>
                        <div className="editor-wrapper">
                          {/* Toolbar */}
                          <div className="editor-toolbar">
                            <EditMarkButton format="bold" label='<i class="bi bi-type-bold"></i>' />
                            <EditMarkButton format="italic" label='<i class="bi bi-type-italic"></i>' />
                            <EditMarkButton format="underline" label='<i class="bi bi-type-underline"></i>' />
                            <EditBlockButton format="heading-one" label='<i class="bi bi-type-h1"></i>' />
                            <EditBlockButton format="heading-two" label='<i class="bi bi-type-h2"></i>' />
                            <EditBlockButton format="heading-three" label='<i class="bi bi-type-h3"></i>' />
                            <EditBlockButton format="paragraph" label='<i class="bi bi-paragraph"></i>' />
                            <EditBlockButton format="bulleted-list" label='<i class="bi bi-list-task"></i>' />
                            <EditBlockButton format="numbered-list" label='<i class="bi bi-list-ol"></i>' />
                          </div>
                          {/* Editor */}
                          <div className="editor-container">
                            <Slate key={`slate-${group?.id}-${showEdit}`} editor={editEditor} initialValue={editEditorValue && editEditorValue.length > 0 ? editEditorValue : [{ type: 'paragraph', children: [{ text: '' }] }]} onChange={setEditEditorValue}>
                             <Editable 
                                placeholder="Type your event description here..."
                                style={{ outline: 'none', minHeight: '120px', fontFamily: 'inherit', fontSize: 'inherit' }}
                                renderElement={({ attributes, children, element }) => {
                                  switch (element.type) {
                                    case 'heading-one':
                                      return <h1 {...attributes} style={{ fontSize: '24px', fontWeight: 'bold', margin: '10px 0' }}>{children}</h1>;
                                    case 'heading-two':
                                      return <h2 {...attributes} style={{ fontSize: '20px', fontWeight: 'bold', margin: '10px 0' }}>{children}</h2>;
                                    case 'heading-three':
                                      return <h3 {...attributes} style={{ fontSize: '16px', fontWeight: 'bold', margin: '10px 0'}}>{children}</h3>;
                                    case 'bulleted-list':
                                      return <li {...attributes} style={{ marginLeft: '20px', listStyleType: 'disc' }}>{children}</li>;
                                    case 'numbered-list':
                                      return <li {...attributes} style={{ marginLeft: '20px', listStyleType: 'decimal' }}>{children}</li>;
                                    default:
                                      return <p {...attributes}>{children}</p>;
                                  }
                                }}
                                renderLeaf={({ attributes, children, leaf }) => {
                                  if (leaf.bold) children = <strong>{children}</strong>;
                                  if (leaf.italic) children = <em>{children}</em>;
                                  if (leaf.underline) children = <u>{children}</u>;
                                  return <span {...attributes}>{children}</span>;
                                }}
                              />
                            </Slate>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Public & Join Only Settings */}
                    <div className="row mb-5">
                      <div className="col-6">
                        <label htmlFor="edit-group-public" className="form-label">Group Findable?</label>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="edit-group-public"
                            checked={editGroupPublic === "Y"}
                            onChange={(e) => setEditGroupPublic(e.target.checked ? "Y" : "N")}
                          />
                          <label className="form-check-label" htmlFor="edit-group-public">
                            {editGroupPublic === "Y" ? "Yes" : "No"}
                          </label>
                        </div>
                      </div>

                      <div className="col-6">
                        <label htmlFor="edit-group-joinonly" className="form-label">Request to join Only</label>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="edit-group-joinonly"
                            checked={editGroupJoinOnly === "Y"}
                            onChange={(e) => setEditGroupJoinOnly(e.target.checked ? "Y" : "N")}
                          />
                          <label className="form-check-label" htmlFor="edit-group-joinonly">
                            {editGroupJoinOnly === "Y" ? "Yes" : "No"}
                          </label>
                        </div>
                      </div>
                    </div>
                                
                    <p className="form-label">Social Media Handles</p>
                    <div className="row mb-5">
                      <div className="col-md-4 col-sm-12 mb-2">
                        <label htmlFor="edit-group-instagram" className="form-label">
                          Instagram 
                        </label>
                        <input type="text" className="form-control" id="edit-group-instagram" value={editGroupInstagram} onChange={(e) => setEditGroupInstagram(e.target.value)}/>
                      </div>
                      <div className="col-md-4 col-sm-12 mb-2">
                        <label htmlFor="edit-group-youtube" className="form-label">
                          Youtube 
                        </label>
                        <input type="text" className="form-control" id="edit-group-youtube" value={editGroupYoutube} onChange={(e) => setEditGroupYoutube(e.target.value)}/>
                      </div>
                      <div className="col-md-4 col-sm-12 mb-2">
                        <label htmlFor="edit-group-twitter" className="form-label">
                          Twitter  
                        </label>
                        <input type="text" className="form-control" id="edit-group-twitter" value={editGroupTwitter} onChange={(e) => setEditGroupTwitter(e.target.value)}/>
                      </div>
                    </div>

   
                    <div className="row mb-5">
                      <div className="col-6">
                        <label htmlFor="edit-group-showmembercount" className="form-label">Display Member Count</label>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="edit-group-showmembercount"
                            checked={editShowMemberCount === "Y"}
                            onChange={(e) => setEditShowMemberCount(e.target.checked ? "Y" : "N")}
                          />
                          <label className="form-check-label" htmlFor="edit-group-showmembercount">
                            {editShowMemberCount === "Y" ? "Yes" : "No"}
                          </label>
                        </div>
                      </div>
                    </div>
                           

                    {/* Save Button */}
                    <div className="row">
                      <div className="col-12">
                        <button className="btn btn-primary" onClick={handleSaveGroupChanges}>
                          Save Changes
                        </button>
                      </div>
                    </div>

                  </div>
                </div>

                {/* Members Tab */}
                <div className="tab-pane fade" id="nav-members" role="tabpanel" aria-labelledby="nav-members-tab">
                  <div className="mt-5">
                                        
                    <div className="row justify-content-between align-items-end m-3 mt-4 mb-0">
                      <div className="col-sm-12 "><h2>View Group Members </h2>
                        <p className="card-text text-gray-500">See the list of members, you can also promote or remove members here.</p>
                      </div>
                    </div>

                    <div className="filter-section"></div>
    
                    {groupMembers.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table" style={{ backgroundColor: "var(--card-bg)", color: "var(--text)" }}>
                          <thead style={{ backgroundColor: "var(--secondary-card-bg)", borderColor: "var(--input-border)" }}>
                            <tr>
                              <th style={{ color: "var(--text)" }}>Display Name</th>
                              <th style={{ color: "var(--text)" }}>Rank</th>
                              <th style={{ color: "var(--text)" }}>Joined</th>
                              <th style={{ color: "var(--text)" }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupMembers.map((member) => {
                              // Check if current user can manage this member
                              const canManage = (userRank === "OWNER") || 
                                (userRank === "ADMIN" && member.GROUP_RANK !== "OWNER" && member.GROUP_RANK !== "ADMIN");
                              
                              const editingRole = memberRoleEdit[member.userId] !== undefined ? memberRoleEdit[member.userId] : member.GROUP_RANK;
                              
                              return (
                                <tr key={member.userId} style={{ borderColor: "var(--input-border)" }}>
                                  <td style={{ color: "var(--event-text)" }}>{member.userData?.USERDISPLAYNAME || "Unknown"}</td>
                                  <td>
                                    <span className={`badge ${member.GROUP_RANK === 'OWNER' ? 'bg-danger' : member.GROUP_RANK === 'ADMIN' ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                                      {member.GROUP_RANK}
                                    </span>
                                  </td>
                                  <td className="small" style={{ color: "var(--event-text)" }}>{member.JOINED_DATE}</td>
                                  <td>
                                    {canManage ? (
                                      <div className="d-flex flex-column gap-2">
                                        <select
                                          className="form-select "
                                          value={editingRole}
                                          onChange={(e) => setMemberRoleEdit({...memberRoleEdit, [member.userId]: e.target.value})}
                                        >
                                          <option value="MEMBER">Member</option>
                                          <option value="ADMIN">Admin</option>
                                          {userRank === "OWNER" && <option value="OWNER">Owner</option>}
                                        </select>
                                        <div className="d-flex gap-2">
                                          <button
                                            className="btn-tertiary-outline flex-grow-1"
                                            onClick={() => handleUpdateMemberRole(member.userId, editingRole)}
                                            title="Update role"
                                            style={{ opacity: editingRole !== member.GROUP_RANK ? 1 : 0.5, pointerEvents: editingRole !== member.GROUP_RANK ? 'auto' : 'none' }}
                                          >
                                           Update
                                          </button>
                                          <button
                                            className="btn-danger flex-grow-1"
                                            onClick={() => setMemberRoleEdit({...memberRoleEdit, [member.userId]: undefined})}
                                            title="Cancel"
                                            style={{ opacity: editingRole !== member.GROUP_RANK ? 1 : 0.5, pointerEvents: editingRole !== member.GROUP_RANK ? 'auto' : 'none' }}
                                          >
                                            Cancel
                                          </button>
                                          <button
                                            className="btn-danger"
                                            onClick={() => handleRemoveMember(member.userId)}
                                            title="Remove member"
                                          >
                                            Remove
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="text-muted small">-</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="alert alert-info" role="alert">
                        <i className="bi bi-info-circle me-2"></i>
                        No members yet.
                      </div>
                    )}
                  </div>
                </div>

                {/* Requests Tab */}
                <div className="tab-pane fade" id="nav-requests" role="tabpanel" aria-labelledby="nav-requests-tab">
                  <div className="mt-5">

                    <div className="row justify-content-between align-items-end m-3 mt-4 mb-0">
                      <div className="col-sm-12 "><h2>View Pending Requests </h2>
                        <p className="card-text text-gray-500">Accept or reject pending group requests.</p>
                      </div>
                    </div>

                    <div className="filter-section"></div>

                   {/* Pending Requests Section - Only for OWNER and ADMIN */}
                    {pendingRequests.length > 0 ? (
                        <div className="card mb-4" style={{ backgroundColor: "var(--card-bg)"}}>
                            <div className="card-body p-0">
                            {pendingRequests.map((request) => (
                                <div key={request.userId} className="p-3" >
                                <div className="d-flex justify-content-between align-items-center mb-2 gap-3">
                                    <div  className="flex-grow-1">
                                    <p className="mb-1 fw-bold" style={{ color: "var(--text)" }}>{request.userData?.USERDISPLAYNAME || "Unknown User"}</p>
                                    </div>
                             
                                    <button
                                    className="btn-tertiary-outline flex-grow-1"
                                    onClick={() => handleAcceptMember(request.userId)}
                                    >
                                    <i className="bi bi-check-circle yes-text me-2"></i>Accept
                                    </button>
                                    <button
                                    className="btn-tertiary-outline flex-grow-1"
                                    onClick={() => handleRejectMember(request.userId)}
                                    >
                                    <i className="bi bi-x-circle no-text me-2"></i>Reject
                                    </button>
                                </div>
                                </div>
                            ))}
                            </div>
                        </div>
                    ) : (
                      <div className="alert alert-info" role="alert" style={{ backgroundColor: "var(--secondary-card-bg)", borderColor: "var(--input-border)", color: "var(--text)" }}>

                        No pending requests.
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>
          </div>
        )}

        </div>
      </div>

      {/* YouTube Embed Modal */}
      {showYoutubeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'var(--card-bg)',
            borderRadius: '12px',
            padding: '28px',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
          }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 style={{ color: 'var(--text)' }} className="mb-0">
                <i className="bi bi-play-circle me-2"></i>Insert YouTube Video
              </h5>
              <button 
                className="btn btn-close" 
                onClick={() => {
                  setShowYoutubeModal(false);
                  setYoutubeUrl("");
                }}
              ></button>
            </div>

            <p style={{ color: 'var(--text)', fontSize: '0.95rem', marginBottom: '1rem' }}>
              Paste a YouTube video URL and it will be embedded in your announcement.
            </p>

            <div className="mb-3">
              <label className="form-label" style={{ color: 'var(--text)' }}>YouTube URL</label>
              <input 
                type="text" 
                className="form-control" 
                placeholder="https://youtube.com/watch?v=... or https://youtu.be/..."
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    insertYoutubeEmbed();
                  }
                }}
              />
            </div>

            <div className="d-flex justify-content-between gap-2">
              <button 
                className="btn btn-danger"
                onClick={() => {
                  setShowYoutubeModal(false);
                  setYoutubeUrl("");
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={insertYoutubeEmbed}
                disabled={!youtubeUrl.trim()}
              >
                <i className="bi bi-plus me-2"></i>Embed Video
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default Group;

